import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, NavigateFunction, Location } from "react-router-dom";
import {
  useForm,
  useFieldArray
} from "react-hook-form";
import { useB2BAuth, B2BFormField, useB2BFormField } from "../../B2BShared";
import { useB2BFormLoadingState } from "../../B2BShared/hooks/useB2BLoadingState";
import { useB2BTranslation } from "../../B2BShared/hooks/useB2BTranslation";
import { Plus, Trash2, AlertCircle, CheckCircle } from "lucide-react";
import B2BBackButton from "../../B2BShared/B2BBackButton/B2BBackButton";
import B2BDeleteProfileButton from "../../B2BShared/B2BDeleteProfileButton/B2BDeleteProfileButton";
import B2BSaveProfileButton from "../../B2BShared/B2BSaveProfileButton/B2BSaveProfileButton";
import { api } from "../../../../utils/apiConfig";
import { User, SocialMediaLink } from "../../../../types";
import { sanitizeFormData, sanitizeBusinessData } from "../../../../utils/sanitization";
import "./B2BProfileCreate.css";

// TYPE DEFINITIONS

interface BusinessInfo {
  businessName: string;
  businessCategory: string[];   // Array: a business can belong to multiple categories (up to 3)
  otherBusinessCategory?: string;
  businessWebsite: string;
  businessWebsiteType?: string;
  companyPhoneCountryCode: string;
  companyPhoneNumber: string;
  businessDescription?: string;
  countriesOfOperation: string[];
  otherCountry?: string;
  socialMedia: SocialMediaLink[];
  socialMediaType?: string; // "has-social-media" or "no-social-media"
  businessLogo: File | null;
}

interface ProfileFormData {
  memberId: string;
  name: string;
  surname: string;  
  countryCode: string;
  phoneNumber: string;
  email: string;
  position: string[];    // Array: users can select multiple positions (up to 3)
  linkedin: string;
  linkedinUrl?: string;
  profilePhoto: File | null;
  businesses: BusinessInfo[];
  interests: string[];
}


// CONSTANTS
const POSITION_VALUES: readonly string[] = [
  "owner",
  "ceo", 
  "chiefOfBoard",
  "anyCxo",
  "president",
  "vicePresident",
  "director",
  "manager",
  "seniorManager",
  "executiveDirector",
  "founder",
  "coFounder",
  "partner",
  "seniorPartner",
  "consultant",
  "seniorConsultant",
  "advisor",
  "boardMember",
  "investor",
  "entrepreneur"
] as const;

const BUSINESS_CATEGORIES: readonly string[] = [
  "accommodation", "artAndCulture", "beauty", "concierge", "consulting",
  "education", "entertainment", "finance", "foodAndBeverages", "health",
  "immigration", "insurance", "it", "legal", "realEstate", "recruitment",
  "restaurants", "tax", "transport",
  "travel", "wellness", "other",
] as const;

const COUNTRIES: readonly string[] = [
  "worldwide", "unitedKingdom", "unitedStates", "france", "germany", "italy", "spain", 
  "netherlands", "switzerland", "canada", "australia", "japan", "singapore",
  "unitedArabEmirates", "russia", "china", "india", "brazil", "mexico"
] as const;


const INTERESTS_OPTIONS: readonly string[] = [
  "investments", "clients", "partnerships", "networking", "technologies", "ventures", "others"
] as const;

const SOCIAL_MEDIA_PLATFORMS: readonly string[] = [
  "instagram", "facebook", "telegram", "twitter", "youtube", "linkedin", "other"
] as const;

const DRAFT_STORAGE_KEY = "b2b_profile_draft" as const;


/**
 * Build the combined phone value for the B2BFormField phone component.
 * Expected output format: "+CC NNNNNN" (e.g. "+44 7956468151")
 * Handles all edge cases: null values, duplicated country codes, leading zeros, etc.
 */
function buildPhoneFieldValue(
  userCtx: any | null,
  profileData: any | null
): string {
  // 1. Gather raw values from whichever source is available
  let cc = (profileData?.countryCode || userCtx?.countryCode || "").toString().trim();
  let pn = (profileData?.phoneNumber || userCtx?.phoneNumber || "").toString().trim();

  // 2. Fallback: try localStorage
  if (!pn || !cc) {
    try {
      const stored = localStorage.getItem("b2bUser");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (!cc) cc = (parsed?.countryCode || "").toString().trim();
        if (!pn) pn = (parsed?.phoneNumber || "").toString().trim();
      }
    } catch { /* ignore */ }
  }

  // 3. Normalize: strip spaces, dashes, parentheses, dots from both fields
  cc = cc.replace(/[\s\-().]/g, "");
  pn = pn.replace(/[\s\-().]/g, "");

  // 4. Default country code
  if (!cc) cc = "+44";
  // Ensure cc starts with +
  if (!cc.startsWith("+")) cc = `+${cc}`;

  // 5. If no phone number at all, return empty
  if (!pn) return "";

  // 6. Clean pn: strip country code if already baked in
  const ccDigits = cc.replace("+", ""); // e.g. "44"

  // pn might be "+447956468151" or "447956468151" or "07956468151" or "7956468151"
  if (pn.startsWith(cc)) {
    // "+44" prefix → strip it
    pn = pn.substring(cc.length);
  } else if (pn.startsWith(`+${ccDigits}`)) {
    // "+44" without our cc format
    pn = pn.substring(ccDigits.length + 1);
  } else if (pn.startsWith(ccDigits) && pn.length > ccDigits.length + 4) {
    // "44" prefix and remaining is long enough to be a real number
    pn = pn.substring(ccDigits.length);
  }

  // Strip leading zeros for UK numbers
  if (cc === "+44" && pn.startsWith("0")) {
    pn = pn.substring(1);
  }

  return pn ? `${cc} ${pn}` : "";
}

const B2BProfileCreate: React.FC = () => {
  const navigate: NavigateFunction = useNavigate();
  const location: Location = useLocation();
  const { user, isAuthenticated, updateProfileStatus, refreshUserData }: any = useB2BAuth();
  const { t, tDashboard, tAuth, i18n } = useB2BTranslation();
  const [showEmailVerifiedBanner, setShowEmailVerifiedBanner] = useState<boolean>(false);
  const [emailVerifiedMessage, setEmailVerifiedMessage] = useState<string>("");
  const previousLanguageRef = useRef<string | null>(null);
  
  // Force English language on profile create page - no Russian allowed
  useEffect(() => {
    // Force English on mount and whenever language changes
    if (i18n.language !== "en") {
      i18n.changeLanguage("en");
    }
    
    // Listen for language changes and force back to English if Russian is selected
    const handleLanguageChange = (lng: string) => {
      if (lng === "ru" || lng.startsWith("ru-")) {
        i18n.changeLanguage("en");
      }
    };
    
    i18n.on("languageChanged", handleLanguageChange);
    
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  // Show "Email confirmed" banner when redirected from email verification link
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const verifiedParam = params.get("verified");
    const verifiedFromState =
      (location.state as any)?.emailVerified === true ||
      (location.state as any)?.verified === true;

    if (verifiedParam === "1" || verifiedFromState) {
      setShowEmailVerifiedBanner(true);
      const msgFromState = (location.state as any)?.verifiedMessage;
      if (typeof msgFromState === "string" && msgFromState.trim()) {
        setEmailVerifiedMessage(msgFromState.trim());
      } else {
        setEmailVerifiedMessage(tAuth("emailAlreadyVerified") || "");
      }
    }
  }, [location.search, location.state, tAuth]);

  // Force English on the profile questionnaire to avoid multi-language data entry.
  // Restores the previous language when leaving the page.
  useEffect(() => {
    if (!previousLanguageRef.current) {
      previousLanguageRef.current = i18n.language || null;
    }
    if (i18n.language !== "en") {
      i18n.changeLanguage("en");
    }
    return () => {
      const prev = previousLanguageRef.current;
      previousLanguageRef.current = null;
      if (prev && prev !== "en") {
        i18n.changeLanguage(prev);
      }
    };
  }, [i18n]);

  const dismissEmailVerifiedBanner = useCallback(() => {
    setShowEmailVerifiedBanner(false);
    setEmailVerifiedMessage("");

    // Remove query/state flags so the banner doesn't immediately come back.
    const params = new URLSearchParams(location.search || "");
    if (params.has("verified")) params.delete("verified");

    const nextSearch = params.toString();
    const nextState: any = location.state ? { ...(location.state as any) } : {};
    delete nextState.emailVerified;
    delete nextState.verified;
    delete nextState.verifiedMessage;

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true, state: nextState }
    );
  }, [location.pathname, location.search, location.state, navigate]);
  
  // Helper function for profile translations
  const tProfile = (key: string) => tDashboard(`profile.${key}`);
  
  const {
    isLoading,
    error,
    handleSubmit: handleFormSubmit,
    setError,
    clearError
  } = useB2BFormLoadingState();
  
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

  const locationState = location.state as any;
  // Detect edit mode from URL path or location state
  const isEditMode: boolean = location.pathname.includes('/edit') || locationState?.isEditing || locationState?.editMode || false;
  const existingProfileData = locationState?.profileData || null;

  
  // URL normalization function
  const normalizeUrl = (url: string): string => {
    if (!url || url.trim() === '') return '';
    
    const trimmedUrl = url.trim();
    
    // Don't normalize "none" values
    if (trimmedUrl.toLowerCase() === "none" || 
        trimmedUrl.toLowerCase() === "https://none" ||
        trimmedUrl.toLowerCase() === "http://none") {
      return "none";
    }
    
    // Remove protocol if present (we'll add it back)
    let urlWithoutProtocol = trimmedUrl.replace(/^https?:\/\//i, '');
    
    // Remove trailing slash
    urlWithoutProtocol = urlWithoutProtocol.replace(/\/$/, '');
    
    // Remove any path after the domain (keep only domain)
    const domainOnly = urlWithoutProtocol.split('/')[0];
    
    // Check if it's a valid domain (has at least one dot)
    if (domainOnly.includes('.')) {
      const parts = domainOnly.split('.');
      const firstPart = parts[0].toLowerCase();
      
      // Common subdomains that shouldn't get www added
      const commonSubdomains = ['mail', 'api', 'www', 'ftp', 'blog', 'shop', 'store', 'app', 'admin', 'cdn', 'static', 'www2', 'www3', 'm', 'mobile', 'en', 'ru', 'fr', 'de', 'es', 'it'];
      
      // If first part is not 'www' and not a common subdomain, add www
      // This handles cases like: globalchic.club -> www.globalchic.club
      if (firstPart !== 'www' && !commonSubdomains.includes(firstPart)) {
        // Only add www if it looks like a simple domain (not already a subdomain)
        // Check if it's a valid domain name (alphanumeric and hyphens only)
        if (/^[a-zA-Z0-9-]+$/.test(parts[0])) {
          urlWithoutProtocol = `www.${urlWithoutProtocol}`;
        }
      }
    }
    
    // Add https:// protocol
    return `https://${urlWithoutProtocol}`;
  };
  
  const generateMemberId = useCallback((): string => {
    const timestamp: string = Date.now().toString(36);
    const random: string = Math.random().toString(36).substr(2, 5);
    return `GC${timestamp}${random}`.toUpperCase();
  }, []);


  const getEmptyBusinessInfo = (): BusinessInfo => ({
    businessName: "",
    businessCategory: [],
    otherBusinessCategory: "",
    businessWebsite: "",
    businessWebsiteType: "has-website",
    companyPhoneCountryCode: "+44",
    companyPhoneNumber: "",
    businessDescription: "",
    countriesOfOperation: [],
    otherCountry: "",
    socialMedia: [],
    socialMediaType: "has-social-media",
    businessLogo: null,
  });

  // UPDATED: Split fullName into name/surname with proper defaults
  const getDefaultValues = useCallback((): ProfileFormData => {
    if (!isEditMode) {
      const savedDraft: string | null = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (savedDraft) {
        try {
          const parsedDraft: Partial<ProfileFormData> = JSON.parse(savedDraft);
          if (parsedDraft && Object.keys(parsedDraft).length > 0) {
            return {
              memberId: "",
              name: "",
              surname: "",
              countryCode: "+44",
              phoneNumber: "",
              email: "",
              position: [],
              linkedin: "",
              linkedinUrl: "",
              profilePhoto: null,
              businesses: [getEmptyBusinessInfo()],
              interests: [],
              ...parsedDraft
            };
          }
        } catch (error: unknown) {
          localStorage.removeItem(DRAFT_STORAGE_KEY);
        }
      }
    }

    if (isEditMode && existingProfileData) {
      // Split fullName if it exists but name/surname don't
      let name = existingProfileData.name || "";
      let surname = existingProfileData.surname || "";
      
      if (!name && !surname && existingProfileData.fullName) {
        const nameParts = existingProfileData.fullName.split(' ');
        name = nameParts[0] || "";
        surname = nameParts.slice(1).join(' ') || "";
      }

      // Build phone value in combined format "+CC NNNNNN" that B2BFormField expects
      const phoneValue = buildPhoneFieldValue(null, existingProfileData);

      // For backward compat: if businesses don't have companyPhone, fall back to top-level
      const topLevelCompanyCC = existingProfileData.companyPhoneCountryCode || "+44";
      const topLevelCompanyPN = existingProfileData.companyPhoneNumber || "";

      const businessesWithPhone = (existingProfileData.businesses?.length
        ? existingProfileData.businesses
        : [getEmptyBusinessInfo()]
      ).map((biz: any, idx: number) => {
        const bizCC = biz.companyPhoneCountryCode || (idx === 0 ? topLevelCompanyCC : "+44");
        const bizPN = biz.companyPhoneNumber || (idx === 0 ? topLevelCompanyPN : "");
        const phoneVal = buildPhoneFieldValue(null, { countryCode: bizCC, phoneNumber: bizPN });
        return {
          ...biz,
          companyPhoneCountryCode: bizCC,
          companyPhoneNumber: phoneVal,
        };
      });

      return {
        memberId: existingProfileData.memberId || generateMemberId(),
        name,
        surname,
        countryCode: existingProfileData.countryCode || "+44",
        phoneNumber: phoneValue,
        email: existingProfileData.email || "",
        position: Array.isArray(existingProfileData.position)
          ? existingProfileData.position
          : existingProfileData.position ? [existingProfileData.position] : [],
        linkedin: existingProfileData.linkedin || "",
        linkedinUrl: "",
        profilePhoto: existingProfileData.profilePhoto || null,
        businesses: businessesWithPhone,
        interests: existingProfileData.interests || [],
      };
    }

    const email: string = locationState?.email || user?.email || "";
    
    // Try to get name/surname from user context
    let name = user?.name || "";
    let surname = user?.surname || "";
    
    // Fallback: split fullName if that's what we have
    if (!name && !surname && user?.fullName) {
      const nameParts = user.fullName.split(' ');
      name = nameParts[0] || "";
      surname = nameParts.slice(1).join(' ') || "";
    }

    // Build phone value in the format the B2BFormField phone type expects: "+CC NNNNNN"
    const phoneValue = buildPhoneFieldValue(user, null);

    return {
      memberId: generateMemberId(),
      name,
      surname,
      countryCode: user?.countryCode || "",
      phoneNumber: phoneValue,
      email,
      position: [],
      linkedin: "",
      linkedinUrl: "",
      profilePhoto: null,
      businesses: [getEmptyBusinessInfo()],
      interests: [],
    };
  }, [isEditMode, existingProfileData, locationState, user, generateMemberId]);

  // ========================================
  // VALIDATION RULES
  // ========================================
  
  // ✅ MIGRATED: Using consolidated form field system with built-in validation

  // ========================================
  // FORM INITIALIZATION
  // ========================================
  
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
    getValues,
    setValue,
    clearErrors,
    setError: setFormFieldError,
    trigger
  } = useForm<ProfileFormData>({
    defaultValues: getDefaultValues(),
    mode: "onSubmit", // Validate on submit to catch all errors
    reValidateMode: "onBlur", // Re-validate on blur after first submit
    criteriaMode: "all",
    shouldUnregister: false
  });

  // Form field hook for consolidated form fields - pass existing form instance
  const { getFieldProps } = useB2BFormField({
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
    clearErrors,
    trigger
  });


  // Update email and phone fields when user context loads
  useEffect(() => {
    if (user?.email && !getValues("email")) {
      setValue("email", user.email);
    }
    
    // Pre-fill phone number from user data if not already set in combined format
    const currentPhoneValue = getValues("phoneNumber");
    const needsPhoneUpdate = !currentPhoneValue || !currentPhoneValue.trim() || !currentPhoneValue.includes(' ');
    
    if (needsPhoneUpdate) {
      // In edit mode, use existingProfileData; in create mode, use user context
      const phoneValue = isEditMode 
        ? buildPhoneFieldValue(user, existingProfileData)
        : buildPhoneFieldValue(user, null);
      if (phoneValue) {
        const cc = phoneValue.split(' ')[0] || "+44";
        setValue("phoneNumber", phoneValue, { shouldValidate: false });
        setValue("countryCode", cc, { shouldValidate: false });
      }
    }
  }, [user, isEditMode, existingProfileData, setValue, getValues]);

  // Keep countryCode form field in sync when phoneNumber field changes
  // (B2BFormField phone component embeds the country code in the combined phoneNumber value,
  //  but does NOT update the separate countryCode form field)
  const watchedPhone = watch("phoneNumber");
  useEffect(() => {
    if (watchedPhone && typeof watchedPhone === 'string' && watchedPhone.includes(' ')) {
      const cc = watchedPhone.split(' ')[0];
      if (cc && cc.startsWith('+')) {
        const currentCC = getValues("countryCode");
        if (currentCC !== cc) {
          setValue("countryCode", cc, { shouldValidate: false });
        }
      }
    }
  }, [watchedPhone, setValue, getValues]);

  // Add a flag to prevent multiple data loads
  const [profileDataLoaded, setProfileDataLoaded] = useState(false);

  useEffect(() => {
    const loadProfileData = async () => {
      if (isAuthenticated && isEditMode && !profileDataLoaded) {
        try {
          const response = await api.getProfile();
          const profileData: User = response.data;
          
          // Populate all form fields with existing data
          if (profileData) {
            setValue("name", profileData.name || "");
            setValue("surname", profileData.surname || "");
            setValue("email", profileData.email || "");
            // Combine country code and phone number for B2BFormField
            const phoneValue = buildPhoneFieldValue(null, profileData);
            setValue("phoneNumber", phoneValue);
            setValue("countryCode", phoneValue ? phoneValue.split(' ')[0] : "+44");
            setValue("position", Array.isArray(profileData.position) 
              ? profileData.position 
              : (profileData.position ? [profileData.position] : []));
            // Handle LinkedIn - if it's "no-linkedin", set it, otherwise set the URL
            const linkedinValue = profileData.linkedin || "";
            if (linkedinValue === "no-linkedin") {
              setValue("linkedin", "no-linkedin");
            } else {
              setValue("linkedin", linkedinValue);
            }
            
            const interestsArray = profileData.interestedIn || [];
            setValue("interests", interestsArray);

            if (profileData.businesses && profileData.businesses.length > 0) {
              const firstBusiness = profileData.businesses[0];
              
              // Set business name
              if (firstBusiness.businessName) {
                setValue("businesses.0.businessName", firstBusiness.businessName);
              }
              
              // Set business website - check for "none", "https://none", "http://none"
              const websiteValue = firstBusiness.businessWebsite?.trim() || "";
              const isNoWebsite = !websiteValue || 
                                 websiteValue === "none" || 
                                 websiteValue === "https://none" || 
                                 websiteValue === "http://none";
              
              if (isNoWebsite) {
                setValue("businesses.0.businessWebsite", "");
                setValue("businesses.0.businessWebsiteType", "no-website");
              } else {
                setValue("businesses.0.businessWebsite", websiteValue);
                setValue("businesses.0.businessWebsiteType", "has-website");
              }
              
              // Set business category (array of strings)
              if (firstBusiness.businessCategory) {
                const cats = Array.isArray(firstBusiness.businessCategory)
                  ? firstBusiness.businessCategory
                  : [firstBusiness.businessCategory];
                // Check for custom categories not in the predefined list
                const knownCats = cats.filter((c: string) => BUSINESS_CATEGORIES.includes(c));
                const customCats = cats.filter((c: string) => !BUSINESS_CATEGORIES.includes(c));
                const finalCats = customCats.length > 0
                  ? [...knownCats, 'other']
                  : knownCats;
                setValue("businesses.0.businessCategory", finalCats);
                if (customCats.length > 0) {
                  setValue("businesses.0.otherBusinessCategory", customCats.join(', '));
                }
              }
              
              // Set business description
              if (firstBusiness.businessDescription) {
                setValue("businesses.0.businessDescription", firstBusiness.businessDescription);
              }
              
              // Set countries of operation
              if (firstBusiness.countriesOfOperation && Array.isArray(firstBusiness.countriesOfOperation)) {
                setValue("businesses.0.countriesOfOperation", firstBusiness.countriesOfOperation);
              }
              
              // Force form to re-render with business data
              setTimeout(() => {
              }, 100);
            }
            
            
            // Populate business information
            if (profileData.businesses && profileData.businesses.length > 0) {
              profileData.businesses.forEach((business: any, index: number) => {
                setValue(`businesses.${index}.businessName`, business.businessName || "");
                
                // Handle business category (array of strings)
                if (business.businessCategory) {
                  const cats = Array.isArray(business.businessCategory)
                    ? business.businessCategory
                    : [business.businessCategory];
                  const knownCats = cats.filter((c: string) => BUSINESS_CATEGORIES.includes(c));
                  const customCats = cats.filter((c: string) => !BUSINESS_CATEGORIES.includes(c));
                  const finalCats = customCats.length > 0
                    ? [...knownCats, 'other']
                    : knownCats;
                  setValue(`businesses.${index}.businessCategory`, finalCats);
                  if (customCats.length > 0) {
                    setValue(`businesses.${index}.otherBusinessCategory`, customCats.join(', '));
                  }
                }
                
                // Handle business website - check for "none", "https://none", "http://none"
                const websiteValue = business.businessWebsite?.trim() || "";
                const isNoWebsite = !websiteValue || 
                                   websiteValue === "none" || 
                                   websiteValue === "https://none" || 
                                   websiteValue === "http://none";
                
                if (isNoWebsite) {
                  setValue(`businesses.${index}.businessWebsite`, "");
                  setValue(`businesses.${index}.businessWebsiteType`, "no-website");
                } else {
                  setValue(`businesses.${index}.businessWebsite`, websiteValue);
                  setValue(`businesses.${index}.businessWebsiteType`, "has-website");
                }
                setValue(`businesses.${index}.businessDescription`, business.businessDescription || "");
                setValue(`businesses.${index}.countriesOfOperation`, business.countriesOfOperation || []);
                
                // Set company phone per business (backward compat: fall back to top-level if API still returns them)
                const legacy = profileData as User & { companyPhoneCountryCode?: string; companyPhoneNumber?: string };
                const bizCC = business.companyPhoneCountryCode || (index === 0 ? legacy.companyPhoneCountryCode : "") || "+44";
                const bizPN = business.companyPhoneNumber || (index === 0 ? legacy.companyPhoneNumber : "") || "";
                const bizPhoneVal = buildPhoneFieldValue(null, { countryCode: bizCC, phoneNumber: bizPN });
                setValue(`businesses.${index}.companyPhoneCountryCode`, bizCC);
                setValue(`businesses.${index}.companyPhoneNumber`, bizPhoneVal);
                
                // Convert social media string back to array format for form
                let socialMediaArray = [];
                let socialMediaType = "has-social-media";
                if (business.socialMedia && typeof business.socialMedia === 'string' && business.socialMedia.trim()) {
                  if (business.socialMedia === "none") {
                    socialMediaType = "no-social-media";
                    socialMediaArray = [];
                  } else {
                    // Parse the string format "platform: url, platform: url" back to array
                    const socialMediaEntries = business.socialMedia.split(',').map(entry => {
                      const [platform, url] = entry.trim().split(':').map(s => s.trim());
                      return { platform: platform || '', url: url || '' };
                    }).filter(entry => entry.platform && entry.url);
                    socialMediaArray = socialMediaEntries;
                  }
                } else if (Array.isArray(business.socialMedia)) {
                  socialMediaArray = business.socialMedia;
                } else if (!business.socialMedia || business.socialMedia === "none") {
                  socialMediaType = "no-social-media";
                }
                setValue(`businesses.${index}.socialMedia`, socialMediaArray);
                setValue(`businesses.${index}.socialMediaType`, socialMediaType);
              });
            }
            
            // Mark profile data as loaded
            setProfileDataLoaded(true);
            
          }
        } catch (error) {
          setError("Failed to load profile data");
        }
      }
    };
    
    loadProfileData();
  }, [isEditMode, isAuthenticated, profileDataLoaded, setValue, setError]);

  const {
    fields: businessFields,
    append: appendBusiness,
    remove: removeBusiness,
  } = useFieldArray({
    control,
    name: "businesses",
  });

  const watchLinkedin: string = watch("linkedin");
  const [shouldScrollToError, setShouldScrollToError] = useState(false);

  // Auto-save state
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const formValuesRef = useRef<ProfileFormData | null>(null);

  const clearDraft = useCallback((): void => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setSaveStatus('idle');
    setLastSavedTime(null);
  }, []);

  // Enhanced save draft function with visual feedback
  const saveDraft = useCallback((data: ProfileFormData): void => {
    try {
      // Exclude file objects from draft (they can't be serialized)
      const draftData = {
        ...data,
        profilePhoto: null,
        businesses: data.businesses.map(business => ({
          ...business,
          businessLogo: null
        }))
      };
      
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
      setLastSavedTime(new Date());
      setSaveStatus('saved');
      
      // Reset status to idle after 3 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('Error saving draft:', error);
      setSaveStatus('error');
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    }
  }, []);

  // Helper function to check if form has meaningful data
  const checkFormHasData = useCallback((formData: any): boolean => {
    // Check if at least one field has meaningful data
    if (formData.name?.trim() || formData.surname?.trim() || formData.email?.trim()) {
      return true;
    }
    if (formData.phoneNumber?.trim()) {
      return true;
    }
    if (Array.isArray(formData.position) ? formData.position.length > 0 : formData.position?.trim()) {
      return true;
    }
    if (formData.businesses?.some((b: any) => b.businessName?.trim() || (Array.isArray(b.businessCategory) ? b.businessCategory.length > 0 : b.businessCategory?.trim()))) {
      return true;
    }
    if (formData.interests?.length > 0) {
      return true;
    }
    return false;
  }, []);

  // Smart auto-save with debouncing (saves 2 seconds after user stops typing)
  useEffect(() => {
    // Watch all form values
    const subscription = watch((value) => {
      // Clear existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Don't auto-save if form is empty or in edit mode (only auto-save drafts in create mode)
      if (isEditMode) {
        return;
      }

      // Check if form has meaningful data before saving
      if (!checkFormHasData(value)) {
        return; // Don't save empty forms
      }

      // Set saving status
      setSaveStatus('saving');

      // Debounce: save 2 seconds after user stops typing
      autoSaveTimeoutRef.current = setTimeout(() => {
        const formData = getValues();
        // Double-check data before saving
        if (checkFormHasData(formData)) {
          formValuesRef.current = formData as ProfileFormData;
          saveDraft(formData as ProfileFormData);
        } else {
          setSaveStatus('idle');
        }
      }, 2000);
    });

    return () => {
      subscription.unsubscribe();
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [watch, getValues, saveDraft, isEditMode, checkFormHasData]);

  // Function to scroll to first error field
  const scrollToFirstError = useCallback((formErrors: any) => {
    // Debug: Log errors to see what we're working with
    if (!formErrors || Object.keys(formErrors).length === 0) {
      console.log('No errors found in scrollToFirstError');
      return;
    }
    
    console.log('Scrolling to first error. Errors:', formErrors);
    
    // Define field order for scrolling (top to bottom) - this is the order fields appear in the form
    // Note: 'business' (singular) is used by react-hook-form for single business validation
    // 'businesses' (plural) is used for array-level validation
    const fieldOrder = [
      'name',
      'surname',
      'phoneNumber',
      'email',
      'position',
      'linkedin',
      'business',      // Singular - for single business validation
      'businesses',    // Plural - for array-level validation
      'interests'
    ];

    // Define business field order (within each business)
    const businessFieldOrder = [
      'businessName',
      'businessCategory',
      'businessWebsite',
      'socialMedia',
      'countriesOfOperation',
      'businessDescription'
    ];

    // Helper function to check if a field has an error (more robust)
    const hasError = (fieldName: string, errors: any): boolean => {
      if (!errors) {
        return false;
      }
      
      // Check if field exists in errors
      if (!(fieldName in errors)) {
        return false;
      }
      
      const error = errors[fieldName];
      
      // Error object with message, type, or just truthy value
      if (error) {
        // Check various error formats from react-hook-form
        if (error.message || error.type || error === true) {
          return true;
        }
        if (typeof error === 'object' && Object.keys(error).length > 0) {
          return true;
        }
        if (typeof error === 'string' && error.length > 0) {
          return true;
        }
        // Even if it's just a truthy value, consider it an error
        return true;
      }
      
      return false;
    };

    // Find the first error field based on field order
    let firstErrorField: string | null = null;
    
    // First, check all main fields in order (excluding business/businesses/interests for now)
    const mainFields = fieldOrder.filter(f => f !== 'business' && f !== 'businesses' && f !== 'interests');
    
    for (const fieldName of mainFields) {
      if (hasError(fieldName, formErrors)) {
        firstErrorField = fieldName;
        break;
      }
    }
    
    // Check business errors (singular - for single business validation)
    if (!firstErrorField && hasError('business', formErrors)) {
      // Try to find the specific business field with error
      const businessError = formErrors.business as any;
      if (businessError && typeof businessError === 'object') {
        // Check business fields in order
        for (const businessFieldName of businessFieldOrder) {
          if (businessError[businessFieldName]) {
            firstErrorField = `businesses.0.${businessFieldName}`;
            break;
          }
        }
        // If no specific field found in error object, check actual form values
        // to find which business field is actually missing
        if (!firstErrorField) {
          try {
            const businesses = getValues("businesses") || [];
            if (businesses.length > 0) {
              const firstBusiness = businesses[0];
              // Check each required field in order to find the first missing one
              // Required fields: businessName, businessCategory, website (or 'none'), countriesOfOperation
              if (!firstBusiness.businessName || !firstBusiness.businessName.trim()) {
                firstErrorField = 'businesses.0.businessName';
              } else if (!firstBusiness.businessCategory || (Array.isArray(firstBusiness.businessCategory) ? firstBusiness.businessCategory.length === 0 : !firstBusiness.businessCategory)) {
                firstErrorField = 'businesses.0.businessCategory';
              } else {
                // Check website requirement: either has a URL or "no-website" checkbox is checked
                const websiteType = firstBusiness.businessWebsiteType || watch(`businesses.0.businessWebsiteType`) || "";
                const hasWebsite = firstBusiness.businessWebsite && firstBusiness.businessWebsite.trim();
                const isNoWebsite = websiteType === "no-website";
                if (!hasWebsite && !isNoWebsite) {
                  // Website is required but not set - scroll to the website field
                  firstErrorField = 'businesses.0.businessWebsite';
                } else if (!firstBusiness.countriesOfOperation || !Array.isArray(firstBusiness.countriesOfOperation) || firstBusiness.countriesOfOperation.length === 0) {
                  firstErrorField = 'businesses.0.countriesOfOperation';
                } else {
                  // If all required fields are filled, but there's still a business error,
                  // it might be a different validation issue - scroll to countries as it's the last required field
                  firstErrorField = 'businesses.0.countriesOfOperation';
                }
              }
            } else {
              // No businesses at all, default to first business field
              firstErrorField = 'businesses.0.businessName';
            }
          } catch (error) {
            // If getValues fails, default to first business field
            firstErrorField = 'businesses.0.businessName';
          }
        }
      } else {
        // Generic business error - check actual form values to find missing field
        try {
          const businesses = getValues("businesses") || [];
          if (businesses.length > 0) {
            const firstBusiness = businesses[0];
            // Check each required field in order to find the first missing one
            // Required fields: businessName, businessCategory, website (or 'none'), countriesOfOperation
            if (!firstBusiness.businessName || !firstBusiness.businessName.trim()) {
              firstErrorField = 'businesses.0.businessName';
            } else if (!firstBusiness.businessCategory || (Array.isArray(firstBusiness.businessCategory) ? firstBusiness.businessCategory.length === 0 : !firstBusiness.businessCategory)) {
              firstErrorField = 'businesses.0.businessCategory';
            } else {
              // Check website requirement: either has a URL or "no-website" checkbox is checked
              const websiteType = firstBusiness.businessWebsiteType || watch(`businesses.0.businessWebsiteType`) || "";
              const hasWebsite = firstBusiness.businessWebsite && firstBusiness.businessWebsite.trim();
              const isNoWebsite = websiteType === "no-website";
              if (!hasWebsite && !isNoWebsite) {
                // Website is required but not set - scroll to the website field
                firstErrorField = 'businesses.0.businessWebsite';
              } else if (!firstBusiness.countriesOfOperation || !Array.isArray(firstBusiness.countriesOfOperation) || firstBusiness.countriesOfOperation.length === 0) {
                firstErrorField = 'businesses.0.countriesOfOperation';
              } else {
                // If all required fields are filled, but there's still a business error,
                // it might be a different validation issue - scroll to countries as it's the last required field
                firstErrorField = 'businesses.0.countriesOfOperation';
              }
            }
          } else {
            // No businesses at all, default to first business field
            firstErrorField = 'businesses.0.businessName';
          }
        } catch (error) {
          // If getValues fails, default to first business field
          firstErrorField = 'businesses.0.businessName';
        }
      }
    }
    
    // Check businesses array errors (plural - for array-level validation)
    if (!firstErrorField && formErrors.businesses) {
      const businessErrors = formErrors.businesses as any;
      if (Array.isArray(businessErrors)) {
        // Find first business with errors
        for (let i = 0; i < businessErrors.length; i++) {
          if (businessErrors[i]) {
            // Check business fields in order
            for (const businessFieldName of businessFieldOrder) {
              if (businessErrors[i][businessFieldName]) {
                firstErrorField = `businesses.${i}.${businessFieldName}`;
                break;
              }
            }
            if (firstErrorField) break;
          }
        }
      }
    }
    
    // Also check for businessWebsite and socialMediaType errors in the businesses array
    if (!firstErrorField && formErrors.businesses) {
      const businessErrors = formErrors.businesses as any;
      if (Array.isArray(businessErrors)) {
        for (let i = 0; i < businessErrors.length; i++) {
          if (businessErrors[i]?.businessWebsite) {
            firstErrorField = `businesses.${i}.businessWebsite`;
            break;
          }
          if (businessErrors[i]?.socialMediaType) {
            firstErrorField = `businesses.${i}.socialMediaType`;
            break;
          }
        }
      }
    }
    
    // Only check interests if no business errors were found
    if (!firstErrorField && hasError('interests', formErrors)) {
      firstErrorField = 'interests';
    }

    // Scroll to the first error field
    if (firstErrorField) {
      console.log('First error field found:', firstErrorField);
      setTimeout(() => {
        let fieldElement: HTMLElement | null = null;
        let scrollTarget: HTMLElement | null = null;
        
        // Special handling for phone number field
        if (firstErrorField === "phoneNumber") {
          // Try to find the phone input container
          const container = document.querySelector(`.phone-input-container`);
          if (container) {
            fieldElement = container.querySelector(`input[type="tel"]`) as HTMLElement;
            scrollTarget = container.closest('.form-group') as HTMLElement || container as HTMLElement;
          }
        } else if (firstErrorField === "linkedin") {
          // Special handling for LinkedIn field - it's a custom input, not B2BFormField
          fieldElement = document.querySelector(`#linkedin, input[name="linkedin"]`) as HTMLElement;
          if (fieldElement) {
            scrollTarget = fieldElement.closest('.form-group') as HTMLElement || fieldElement;
          }
        } else if (firstErrorField === "interests") {
          // Special handling for Interests field - it's a fieldset with checkboxes
          // Try to find the fieldset that contains the interests checkboxes
          const fieldsets = document.querySelectorAll('fieldset');
          let interestsFieldset: HTMLElement | null = null;
          fieldsets.forEach((fs) => {
            const legend = fs.querySelector('legend');
            if (legend && (legend.textContent?.includes('interested') || legend.textContent?.includes('Interested'))) {
              interestsFieldset = fs as HTMLElement;
            }
          });
          const interestsContainer = document.querySelector('.interests-container') as HTMLElement;
          // Try to find the interests section by looking for the legend with "interests" text (but not "interested")
          const allLegends = document.querySelectorAll('legend');
          let interestsSectionLegend: HTMLElement | null = null;
          allLegends.forEach((legend) => {
            const text = legend.textContent || '';
            const lowerText = text.toLowerCase();
            // Look for legend that says "interests" but not "interested in"
            if (lowerText.includes('interests') && !lowerText.includes('interested')) {
              interestsSectionLegend = legend as HTMLElement;
            }
          });
          // Find the form-section that contains the interests
          let interestsSection: HTMLElement | null = null;
          if (interestsSectionLegend) {
            interestsSection = interestsSectionLegend.closest('fieldset.form-section') as HTMLElement;
          }
          // Prefer fieldset > container > section > legend
          fieldElement = interestsFieldset || interestsContainer;
          if (fieldElement) {
            scrollTarget = fieldElement.closest('.form-group') as HTMLElement || 
                          fieldElement.closest('fieldset.form-section') as HTMLElement ||
                          fieldElement;
          } else if (interestsSection) {
            scrollTarget = interestsSection;
          } else if (interestsSectionLegend) {
            scrollTarget = interestsSectionLegend.closest('fieldset.form-section') as HTMLElement || interestsSectionLegend;
          }
        } else if (firstErrorField.startsWith("businesses.")) {
          // Handle nested business fields
          const fieldName = firstErrorField.split('.').pop();
          const businessIndex = parseInt(firstErrorField.split('.')[1]);
          
          // Try multiple strategies to find the field
          // Strategy 1: Direct name/id selector
          fieldElement = document.querySelector(
            `[name="${firstErrorField}"], [id="${firstErrorField}"]`
          ) as HTMLElement;
          
          // Strategy 2: Find by business index and field name
          if (!fieldElement) {
            const businessBlock = document.querySelectorAll('.business-block')[businessIndex];
            if (businessBlock) {
              if (fieldName === 'businessName') {
                fieldElement = businessBlock.querySelector(`input[name*="businessName"]`) as HTMLElement;
              } else if (fieldName === 'businessCategory') {
                fieldElement = businessBlock.querySelector(`select[name*="businessCategory"]`) as HTMLElement;
              } else if (fieldName === 'businessWebsite') {
                // For website, scroll to the website input field
                fieldElement = businessBlock.querySelector(`input[name*="businessWebsite"]`) as HTMLElement;
              } else if (fieldName === 'socialMedia' || fieldName === 'socialMediaType') {
                // For social media, scroll to the social media container or label
                // Try to find the label that contains "Social Media" text
                const labels = businessBlock.querySelectorAll('label');
                let socialMediaLabel: HTMLElement | null = null;
                labels.forEach((label) => {
                  if (label.textContent && label.textContent.includes('Social Media')) {
                    socialMediaLabel = label as HTMLElement;
                  }
                });
                const socialMediaContainer = businessBlock.querySelector('.social-media-container') as HTMLElement;
                const noSocialMediaCheckbox = businessBlock.querySelector(`[id*="no-social-media"]`) as HTMLElement;
                fieldElement = socialMediaContainer || noSocialMediaCheckbox || socialMediaLabel;
              } else if (fieldName === 'countriesOfOperation') {
                // For countries, scroll to the fieldset or legend
                // Try to find the legend that contains "Countries" text within this business block
                const legends = businessBlock.querySelectorAll('legend');
                let countriesLegend: HTMLElement | null = null;
                legends.forEach((legend) => {
                  const text = legend.textContent || '';
                  if (text.includes('Countries') || text.includes('countries') || text.includes('Countries of Operation')) {
                    countriesLegend = legend as HTMLElement;
                  }
                });
                // Try to find the fieldset that contains the countries checkboxes
                const fieldsets = businessBlock.querySelectorAll('fieldset');
                let countriesFieldset: HTMLElement | null = null;
                fieldsets.forEach((fs) => {
                  const legend = fs.querySelector('legend');
                  if (legend && (legend.textContent?.includes('Countries') || legend.textContent?.includes('countries'))) {
                    countriesFieldset = fs as HTMLElement;
                  }
                });
                const countriesContainer = businessBlock.querySelector('.countries-checkbox-container') as HTMLElement;
                // Prefer fieldset > legend > container
                fieldElement = countriesFieldset || countriesLegend || countriesContainer;
              } else if (fieldName === 'businessDescription') {
                fieldElement = businessBlock.querySelector(`textarea[name*="businessDescription"]`) as HTMLElement;
              }
              
              // If we found the field, find its form-group parent
              if (fieldElement) {
                scrollTarget = fieldElement.closest('.form-group') as HTMLElement || 
                              fieldElement.closest('.business-block') as HTMLElement ||
                              fieldElement;
              } else {
                // Fallback: scroll to the business block
                scrollTarget = businessBlock as HTMLElement;
              }
            }
          } else {
            // If we found the element directly, find its form-group
            scrollTarget = fieldElement?.closest('.form-group') as HTMLElement || fieldElement;
          }
        } else {
          // For other fields, try multiple strategies
          // Strategy 1: Find by name or id
          fieldElement = document.querySelector(
            `[name="${firstErrorField}"], [id="${firstErrorField}"]`
          ) as HTMLElement;
          
          // Strategy 2: Find by label's "for" attribute
          if (!fieldElement) {
            const label = document.querySelector(`label[for="${firstErrorField}"]`);
            if (label) {
              const inputId = label.getAttribute('for');
              if (inputId) {
                fieldElement = document.getElementById(inputId) as HTMLElement;
              }
            }
          }
          
          // Strategy 3: Find form-group containing the field
          if (!fieldElement) {
            const formGroups = document.querySelectorAll('.form-group');
            Array.from(formGroups).forEach((group) => {
              if (!fieldElement) {
                const input = group.querySelector(`[name="${firstErrorField}"], [id="${firstErrorField}"]`);
                if (input) {
                  fieldElement = input as HTMLElement;
                  scrollTarget = group as HTMLElement;
                }
              }
            });
          }
          
          // If we found the element but not the scroll target, find its form-group parent
          if (fieldElement && !scrollTarget) {
            scrollTarget = fieldElement.closest('.form-group') as HTMLElement || fieldElement;
          }
        }
        
        // Determine what to scroll to
        const elementToScroll = scrollTarget || fieldElement;
        
        if (elementToScroll) {
          console.log('Scrolling to element:', elementToScroll, 'Field:', firstErrorField);
          elementToScroll.scrollIntoView({ 
            behavior: "smooth", 
            block: "center",
            inline: "nearest"
          });
          
          // Focus the input element if it's focusable
          if (fieldElement && (
            fieldElement instanceof HTMLInputElement || 
            fieldElement instanceof HTMLSelectElement || 
            fieldElement instanceof HTMLTextAreaElement
          )) {
            setTimeout(() => {
              fieldElement?.focus();
            }, 300);
          }
        } else {
          console.warn('Could not find element to scroll to for field:', firstErrorField);
        }
      }, 150);
    } else {
      console.log('No first error field found');
    }
  }, [getValues, watch]);

  // Watch for form errors and scroll to first error when they appear
  useEffect(() => {
    if (shouldScrollToError && Object.keys(errors).length > 0) {
      // Add a small delay to ensure DOM is updated
      setTimeout(() => {
        scrollToFirstError(errors);
        setShouldScrollToError(false);
      }, 200);
    }
  }, [errors, shouldScrollToError, scrollToFirstError]);

  // Handle form submission with error callback
  const handleFormSubmitWrapper = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Clear any previous general errors when attempting to submit
    clearError();
    
    // Use handleSubmit which will validate and call onError if validation fails
    return handleSubmit(
      onSubmit,
      (formErrors) => {
        // This callback is called when validation fails
        console.log('Form validation failed. Errors:', formErrors);
        console.log('FormState errors:', errors);
        
        // Use the errors from the callback (most up-to-date)
        const errorsToUse = formErrors && Object.keys(formErrors).length > 0 ? formErrors : errors;
        
        if (errorsToUse && Object.keys(errorsToUse).length > 0) {
          setShouldScrollToError(true);
          
          // Scroll immediately with the errors from callback
          setTimeout(() => {
            console.log('Scrolling to first error with errors:', errorsToUse);
            scrollToFirstError(errorsToUse);
          }, 200);
        } else {
          console.warn('No errors found in validation callback');
        }
      }
    )(e);
  };


  
  const onSubmit = async (values: ProfileFormData): Promise<void> => {
    return handleFormSubmit(async () => {
      // Clear any previous errors
      setError(null);
      clearError();
      // Sanitize all input data
      const sanitizedValues = sanitizeFormData(values);
      
      // Split combined phone number back into separate fields
      // The B2BFormField phone component stores the value in format: "+CC NNNNNN"
      // We need to split it back into countryCode and phoneNumber for the backend
      let phoneNumberValue: string | null = null;
      let countryCodeValue: string | null = null;
      
      if (values.phoneNumber?.trim()) {
        const phoneValue = values.phoneNumber.trim();
        
        // Primary format: "+CC NNNNNN" (space-separated, from B2BFormField phone component)
        if (phoneValue.includes(' ')) {
          const spaceIdx = phoneValue.indexOf(' ');
          const ccPart = phoneValue.substring(0, spaceIdx).trim();
          const numPart = phoneValue.substring(spaceIdx + 1).replace(/[\s\-()]/g, "").trim();
          
          if (ccPart.startsWith('+') && numPart) {
            countryCodeValue = ccPart;
            phoneNumberValue = numPart;
          } else {
            // Space exists but cc part doesn't look like a country code
            countryCodeValue = "+44";
            phoneNumberValue = phoneValue.replace(/[\s\-()]/g, "").replace(/^\+\d{1,3}/, "");
          }
        } else if (phoneValue.startsWith('+')) {
          // Format: "+447956468151" - no space, try known country codes (longest match first)
          const knownCodes = ['+971', '+886', '+852', '+353', '+372', '+371', '+370', '+356', '+351', '+350', '+49', '+48', '+47', '+46', '+45', '+44', '+43', '+41', '+40', '+39', '+36', '+34', '+33', '+32', '+31', '+30', '+27', '+86', '+81', '+65', '+55', '+52', '+91', '+90', '+82', '+64', '+61', '+60', '+58', '+57', '+56', '+54', '+53', '+51', '+7', '+1'];
          let matched = false;
          for (const code of knownCodes) {
            if (phoneValue.startsWith(code) && phoneValue.length > code.length) {
              countryCodeValue = code;
              phoneNumberValue = phoneValue.substring(code.length).replace(/[\s\-()]/g, "");
              matched = true;
              break;
            }
          }
          if (!matched) {
            // Generic extraction: +X, +XX, or +XXX
            const match = phoneValue.match(/^(\+\d{1,3})(\d+)$/);
            if (match) {
              countryCodeValue = match[1];
              phoneNumberValue = match[2];
            } else {
              countryCodeValue = "+44";
              phoneNumberValue = phoneValue.replace(/^\+/, '').replace(/[\s\-()]/g, "");
            }
          }
        } else {
          // Format: "7956468151" - plain digits, no country code in value
          // Use "+44" as default — do NOT use values.countryCode which may contain stale data
          countryCodeValue = "+44";
          phoneNumberValue = phoneValue.replace(/[\s\-()]/g, "");
        }
      }
      
      // Final normalization: strip any remaining spaces, dashes, parens, dots from phone
      if (phoneNumberValue) {
        phoneNumberValue = phoneNumberValue.replace(/[\s\-().]/g, "");
      }
      if (countryCodeValue) {
        countryCodeValue = countryCodeValue.replace(/[\s\-().]/g, "");
        if (!countryCodeValue.startsWith("+")) countryCodeValue = `+${countryCodeValue}`;
      }
      
      const processedValues: any = {
        name: sanitizedValues.name?.trim() || null,
        surname: sanitizedValues.surname?.trim() || null,
        phoneNumber: phoneNumberValue,
        countryCode: countryCodeValue,
        email: values.email || null,
        position: Array.isArray(values.position) && values.position.length > 0
          ? values.position
          : [],
        linkedin: values.linkedin === "no-linkedin" 
          ? "no-linkedin" 
          : values.linkedin === "custom" 
            ? values.linkedinUrl?.trim() || null
            : values.linkedin ? normalizeUrl(values.linkedin) : null,
        profilePhoto: values.profilePhoto ? "placeholder-profile-photo-url" : undefined, // Handle file upload later
        
        businesses: sanitizedValues.businesses?.filter((business: BusinessInfo) => 
          Boolean(business.businessName?.trim()) || (Array.isArray(business.businessCategory) ? business.businessCategory.length > 0 : Boolean(business.businessCategory))
        ).map((business: BusinessInfo) => {
          // Parse company phone per business
          let bizPhoneNum: string | null = null;
          let bizPhoneCC: string | null = null;
          if (business.companyPhoneNumber?.trim()) {
            const cpVal = business.companyPhoneNumber.trim();
            if (cpVal.includes(' ')) {
              const spIdx = cpVal.indexOf(' ');
              const ccP = cpVal.substring(0, spIdx).trim();
              const numP = cpVal.substring(spIdx + 1).replace(/[\s\-()]/g, "").trim();
              if (ccP.startsWith('+') && numP) {
                bizPhoneCC = ccP;
                bizPhoneNum = numP;
              }
            } else if (cpVal.startsWith('+')) {
              bizPhoneCC = "+44";
              bizPhoneNum = cpVal.replace(/^\+\d{1,3}/, "").replace(/[\s\-()]/g, "");
            } else {
              bizPhoneCC = "+44";
              bizPhoneNum = cpVal.replace(/[\s\-()]/g, "");
            }
          }
          return sanitizeBusinessData({
          businessName: business.businessName?.trim() || null,
          businessWebsite: business.businessWebsiteType === "no-website" 
            ? "none"
            : (business.businessWebsite?.trim() 
              ? (business.businessWebsite.trim().toLowerCase() === "none" || 
                 business.businessWebsite.trim().toLowerCase() === "https://none" ||
                 business.businessWebsite.trim().toLowerCase() === "http://none"
                ? "none"
                : normalizeUrl(business.businessWebsite.trim()))
              : null),
          companyPhoneNumber: bizPhoneNum,
          companyPhoneCountryCode: bizPhoneCC,
          businessCategory: (() => {
            const cats = Array.isArray(business.businessCategory)
              ? business.businessCategory
              : (business.businessCategory ? [business.businessCategory] : []);
            // Replace 'other' with the user-specified custom category
            return cats.map((c: string) => 
              c === 'other' ? (business.otherBusinessCategory?.trim() || 'other') : c
            ).filter((c: string) => c && c.trim().length > 0);
          })(),
          businessDescription: business.businessDescription?.trim() || null,
          productsServices: null, // Add missing field
          // Include "other" country if specified, and handle "worldwide"
          countriesOfOperation: business.countriesOfOperation?.map((country: string) => {
            if (country === 'other' && business.otherCountry?.trim()) {
              return business.otherCountry.trim();
            }
            return country;
          }).filter((c: string) => c && c.trim().length > 0) || [],
          // Social media is optional - send "none" if "no-social-media" is selected, or formatted string if has entries
          socialMedia: business.socialMediaType === "no-social-media" 
            ? "none"
            : (business.socialMedia && Array.isArray(business.socialMedia) && business.socialMedia.length > 0 
              ? business.socialMedia.map(social => `${social.platform}: ${social.url}`).join(', ')
              : "none"),
          logo: business.businessLogo ? "placeholder-logo-url" : null,
        });}) || [],
        
        // Process interests to match backend schema
        interestedIn: values.interests?.length > 0 
          ? values.interests.map((interest: string) => interest.toLowerCase()) 
          : [],
        
      };

      // Validate required fields before sending
      if (!processedValues.position || (Array.isArray(processedValues.position) && processedValues.position.length === 0)) {
        throw new Error("Position is required. Please select at least one position.");
      }

      try {
        // Check if profile is already complete - if so, use update endpoint even in create mode
        const profileAlreadyComplete = user?.profileComplete || false;
        const shouldUseUpdate = isEditMode || profileAlreadyComplete;
        
        const response = shouldUseUpdate
          ? await api.updateUserProfile(processedValues as any)
          : await api.createProfile(processedValues as any);

        if (response.success) {
          clearDraft();
          updateProfileStatus(true);
          
          // Refresh user data to get updated approval status
          if (refreshUserData) {
            await refreshUserData();
          }
          
          // Get updated user status after refresh
          const updatedUserStr = localStorage.getItem("b2bUser");
          let updatedUser: any = null;
          if (updatedUserStr) {
            try {
              updatedUser = JSON.parse(updatedUserStr);
            } catch (e) {
              console.error("Error parsing updated user:", e);
            }
          }
          
          const isApproved = updatedUser?.isApproved || user?.isApproved || false;
          const isAdmin = updatedUser?.isAdmin || user?.isAdmin || false;
          
          // Determine redirect based on user status and mode
          if (isEditMode) {
            // Editing existing profile
            if (isApproved || isAdmin) {
              // Approved user editing - show success modal with option to stay or go to dashboard
              setShowSuccessModal(true);
            } else {
              // Unapproved user editing - redirect to waiting approval (no modal)
              navigate("/gcb/waiting-approval", { replace: true });
              return;
            }
          } else {
            // Creating new profile
            if (isApproved || isAdmin) {
              // Already approved (shouldn't happen for new profiles, but handle it)
              navigate("/gcb/dashboard", { replace: true });
              return;
            } else {
              // New profile needs approval - redirect immediately (no modal)
              navigate("/gcb/waiting-approval", { replace: true });
              return;
            }
          }
        } else {
          // Build detailed error message from backend response
          // Cast to any to access error details that may not be in the type definition
          const errorResponse = response as any;
          let errorMessage: string = errorResponse.message || response.message || `Profile ${isEditMode ? "update" : "creation"} failed`;
          
          // If backend provides details about missing fields, include them
          if (errorResponse.details) {
            const missingFields: string[] = [];
            Object.entries(errorResponse.details).forEach(([field, message]: [string, any]) => {
              if (message) {
                missingFields.push(message);
              }
            });
            
            if (missingFields.length > 0) {
              errorMessage = `${errorMessage}\n\nMissing required fields:\n${missingFields.map(field => `• ${field}`).join('\n')}`;
            }
          }
          
          // Also check missingFields array if provided
          if (errorResponse.missingFields && Array.isArray(errorResponse.missingFields) && errorResponse.missingFields.length > 0) {
            const fieldLabels: Record<string, string> = {
              name: "Name (minimum 2 characters)",
              surname: "Surname (minimum 2 characters)",
              phoneNumber: "Phone number (minimum 7 characters)",
              position: "Position",
              hasBusiness: "At least one business with name, category, and countries of operation",
              hasInterests: "At least one interest"
            };
            
            const missingFieldMessages = errorResponse.missingFields.map((field: string) => {
              return fieldLabels[field] || field;
            });
            
            if (missingFieldMessages.length > 0) {
              errorMessage = `${errorMessage}\n\nMissing required fields:\n${missingFieldMessages.map(field => `• ${field}`).join('\n')}`;
            }
          }
          
          throw new Error(errorMessage);
        }
      } catch (error: any) {
        // Handle network errors (Failed to fetch)
        if (error.message === "Failed to fetch" || error.name === "TypeError") {
          const networkErrorMessage = `Network error: Unable to connect to the server. This might be because:
• The server is temporarily unavailable
• Your internet connection is unstable
• The server is taking too long to respond

Please check your internet connection and try again. If the problem persists, the server might be restarting.`;
          setError(networkErrorMessage);
          throw new Error(networkErrorMessage);
        }
        
        // Handle API errors - check if error has response data with details
        let errorMessage = error.message || `Profile ${isEditMode ? "update" : "creation"} failed`;
        
        // Check if error has response.data (from API error response)
        const errorData = error.response?.data || error.response || {};
        
        // Check for "Profile already complete" error - retry with update endpoint
        if (errorData.message && errorData.message.includes("Profile already complete")) {
          // Profile already exists, using update endpoint instead
          try {
            const updateResponse = await api.updateUserProfile(processedValues as any);
            if (updateResponse.success) {
              clearDraft();
              updateProfileStatus(true);
              if (refreshUserData) {
                await refreshUserData();
              }
              setShowSuccessModal(true);
              return; // Success, exit early
            }
          } catch (updateError: any) {
            // If update also fails, continue with original error handling
          }
        }
        
        // Check for validation errors array (from express-validator)
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const validationErrors = errorData.errors.map((err: any) => {
            return err.message || `${err.field || err.param || 'Field'}: ${err.msg || 'Invalid value'}`;
          });
          
          if (validationErrors.length > 0) {
            errorMessage = `${errorData.message || "Validation failed"}\n\nValidation errors:\n${validationErrors.map(err => `• ${err}`).join('\n')}`;
          }
        }
        
        // Check for details object
        if (errorData.details) {
          // Map backend field names to form field names
          const fieldMapping: Record<string, string> = {
            name: "name",
            surname: "surname",
            phoneNumber: "phoneNumber",
            position: "position",
            countryCode: "phoneNumber", // Country code is part of phone number field
            hasBusiness: "businesses",
            hasInterests: "interests"
          };
          
          // Enhanced field-specific error messages with helpful suggestions
          const fieldErrorMessages: Record<string, string> = {
            name: "Name is required and must be at least 2 characters. Please enter your first name.",
            surname: "Surname is required and must be at least 2 characters. Please enter your last name.",
            phoneNumber: "Phone number is required. Please include your country code (e.g., +44 for UK) followed by your phone number. Minimum 7 digits required.",
            countryCode: "Phone number is required. Please include your country code (e.g., +44 for UK) followed by your phone number. Minimum 7 digits required.",
            position: "Please select at least one position (up to 3). This helps us understand your role in the business.",
            hasBusiness: "At least one business is required. Please provide:\n• Business name\n• Business category\n• Website (or select 'No website')\n• At least one country of operation",
            hasInterests: "Please select at least one area of interest. This helps us connect you with relevant opportunities."
          };
          
          // Helpful suggestions for each field
          const fieldSuggestions: Record<string, string> = {
            name: "💡 Tip: Enter your legal first name as it appears on official documents.",
            surname: "💡 Tip: Enter your legal last name as it appears on official documents.",
            phoneNumber: "💡 Tip: Format: +[country code] [phone number]. Example: +44 7956468151",
            countryCode: "💡 Tip: Format: +[country code] [phone number]. Example: +44 7956468151",
            position: "💡 Tip: Select up to 3 positions that best describe your role(s).",
            hasBusiness: "💡 Tip: You can add multiple businesses. Make sure each has a name, category, and at least one country.",
            hasInterests: "💡 Tip: Select all areas that interest you. You can choose multiple options."
          };
          
          let firstFieldToFocus: string | null = null;
          const missingFields: string[] = [];
          
          const errorSuggestions: string[] = [];
          
          Object.entries(errorData.details).forEach(([field, message]: [string, any]) => {
            if (message) {
              missingFields.push(message);
              
              const formFieldName = fieldMapping[field] || field;
              const errorMessage = fieldErrorMessages[field] || message || `Please complete the ${field} field`;
              const suggestion = fieldSuggestions[field];
              
              // Set field error using react-hook-form with enhanced message
              setFormFieldError(formFieldName as any, {
                type: "validation",
                message: errorMessage
              });
              
              // Collect suggestions
              if (suggestion) {
                errorSuggestions.push(suggestion);
              }
              
              // Track first field to focus
              if (!firstFieldToFocus && formFieldName !== "businesses" && formFieldName !== "interests") {
                firstFieldToFocus = formFieldName;
              }
            }
          });
          
          // Scroll to and focus the first problematic field
          if (firstFieldToFocus) {
            setTimeout(() => {
              let fieldElement: HTMLElement | null = null;
              
              // Special handling for phone number field
              if (firstFieldToFocus === "phoneNumber") {
                // Try to find the phone number input (the actual number input, not the country code select)
                fieldElement = document.querySelector(`.phone-number-input`) as HTMLElement;
                // If not found, try to find the phone input container
                if (!fieldElement) {
                  const container = document.querySelector(`.phone-input-container`);
                  if (container) {
                    fieldElement = container.querySelector(`input[type="tel"], input[name*="phone"]`) as HTMLElement || container as HTMLElement;
                  }
                }
              } else {
                // For other fields, use standard selectors
                fieldElement = document.querySelector(`[name="${firstFieldToFocus}"], [id="${firstFieldToFocus}"]`) as HTMLElement;
              }
              
              if (fieldElement) {
                fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
                // Only focus if it's an input element
                if (fieldElement instanceof HTMLInputElement || fieldElement instanceof HTMLSelectElement || fieldElement instanceof HTMLTextAreaElement) {
                  fieldElement.focus();
                }
              }
            }, 100);
          }
          
          if (missingFields.length > 0) {
            const fieldsCount = missingFields.length;
            errorMessage = `Please complete ${fieldsCount === 1 ? 'the required field' : `all ${fieldsCount} required fields`} below. ${fieldsCount === 1 ? 'The field has been highlighted for you.' : 'The first field has been highlighted for you.'}`;
            
            // Add helpful suggestions to error message
            if (errorSuggestions.length > 0) {
              errorMessage += `\n\n${errorSuggestions.join('\n')}`;
            }
            
            setError(errorMessage);
            // Don't throw - return early to prevent loading state from getting stuck
            return;
          }
        }
        
        // Also check missingFields array if provided
        if (errorData.missingFields && Array.isArray(errorData.missingFields) && errorData.missingFields.length > 0) {
          // Map backend field names to form field names
          const fieldMapping: Record<string, string> = {
            name: "name",
            surname: "surname",
            phoneNumber: "phoneNumber",
            position: "position",
            hasBusiness: "businesses",
            hasInterests: "interests"
          };
          
          // Field-specific error messages
          const fieldErrorMessages: Record<string, string> = {
            name: "Name must be at least 2 characters",
            surname: "Surname must be at least 2 characters",
            phoneNumber: "Phone number must be at least 7 digits. Please include your country code and phone number.",
            position: "Please select a position",
            hasBusiness: "At least one business with name, category, and countries of operation is required",
            hasInterests: "At least one interest is required"
          };
          
          let firstFieldToFocus: string | null = null;
          
          // Set field-specific errors and find first field to focus
          errorData.missingFields.forEach((field: string) => {
            const formFieldName = fieldMapping[field] || field;
            const errorMessage = fieldErrorMessages[field] || `Please complete the ${field} field`;
            
            // Set field error using react-hook-form
            setFormFieldError(formFieldName as any, {
              type: "validation",
              message: errorMessage
            });
            
            // Track first field to focus
            if (!firstFieldToFocus && formFieldName !== "businesses" && formFieldName !== "interests") {
              firstFieldToFocus = formFieldName;
            }
          });
          
          // Scroll to and focus the first problematic field
          if (firstFieldToFocus) {
            setTimeout(() => {
              let fieldElement: HTMLElement | null = null;
              
              // Special handling for phone number field
              if (firstFieldToFocus === "phoneNumber") {
                // Try to find the phone number input (the actual number input, not the country code select)
                fieldElement = document.querySelector(`.phone-number-input`) as HTMLElement;
                // If not found, try to find the phone input container
                if (!fieldElement) {
                  const container = document.querySelector(`.phone-input-container`);
                  if (container) {
                    fieldElement = container.querySelector(`input[type="tel"], input[name*="phone"]`) as HTMLElement || container as HTMLElement;
                  }
                }
              } else {
                // For other fields, use standard selectors
                fieldElement = document.querySelector(`[name="${firstFieldToFocus}"], [id="${firstFieldToFocus}"]`) as HTMLElement;
              }
              
              if (fieldElement) {
                fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
                // Only focus if it's an input element
                if (fieldElement instanceof HTMLInputElement || fieldElement instanceof HTMLSelectElement || fieldElement instanceof HTMLTextAreaElement) {
                  fieldElement.focus();
                }
              }
            }, 100);
          }
          
          // Show a user-friendly general error message
          const fieldLabels: Record<string, string> = {
            name: "Name (minimum 2 characters)",
            surname: "Surname (minimum 2 characters)",
            phoneNumber: "Phone number (minimum 7 characters)",
            position: "Position",
            hasBusiness: "At least one business with name, category, and countries of operation",
            hasInterests: "At least one interest"
          };
          
          const missingFieldMessages = errorData.missingFields.map((field: string) => {
            return fieldLabels[field] || field;
          });
          
          if (missingFieldMessages.length > 0) {
            errorMessage = `Please complete the required fields below. ${missingFieldMessages.length === 1 ? 'The field has been highlighted for you.' : 'The first field has been highlighted for you.'}`;
            setError(errorMessage);
            // Don't throw - return early to prevent loading state from getting stuck
            return;
          }
        }
        
        // If we get here, it's a different type of error
        setError(errorMessage);
        // Don't throw - return early to prevent loading state from getting stuck
        // The error is already displayed via setError
        return;
      }
    });
  };

  
  const handleAddBusiness = useCallback((e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      const currentBusinesses: BusinessInfo[] = getValues("businesses") || [];
      
      // Limit to maximum 3 businesses
      if (currentBusinesses.length >= 3) {
        setError("You can add a maximum of 3 businesses.");
        return;
      }
      
      const lastBusiness: BusinessInfo | undefined = currentBusinesses[currentBusinesses.length - 1];
      
      // Only show warning if user has filled in multiple fields but not the name
      // This allows adding businesses freely, but warns if they've clearly started filling one
      if (lastBusiness) {
        const filledFields = [
          Array.isArray(lastBusiness.businessCategory) ? lastBusiness.businessCategory.length > 0 : !!lastBusiness.businessCategory,
          lastBusiness.businessWebsite,
          lastBusiness.businessDescription,
          lastBusiness.countriesOfOperation?.length > 0,
          lastBusiness.socialMedia?.length > 0
        ].filter(Boolean).length;
        
        // Only warn if they've filled 2+ fields but not the name
        if (filledFields >= 2 && !lastBusiness.businessName?.trim()) {
          setError("Please complete the current business information (especially the business name) before adding another.");
          return;
        }
      }
      
      const newBusiness = getEmptyBusinessInfo();
      const newIndex = currentBusinesses.length;
      
      // Store current scroll position to prevent auto-scroll
      const scrollY = window.scrollY;
      
      // Clear any existing validation errors before appending
      clearErrors();
      clearError();
      
      // Append the new business without focusing
      appendBusiness(newBusiness, { shouldFocus: false });
      
      // Immediately clear validation errors for the new business fields
      // This prevents any auto-validation from triggering on the new empty fields
      requestAnimationFrame(() => {
        clearErrors([
          `businesses.${newIndex}.businessName`,
          `businesses.${newIndex}.businessCategory`,
          `businesses.${newIndex}.businessWebsite`,
          `businesses.${newIndex}.countriesOfOperation`,
          `businesses.${newIndex}.businessDescription`,
          `businesses.${newIndex}`
        ]);
        
        // Prevent any auto-scroll behavior by restoring scroll position
        window.scrollTo({ top: scrollY, behavior: 'auto' });
        
        // Also blur any focused elements to prevent auto-focus
        if (document.activeElement && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      });
    } catch (error) {
      console.error("Error adding business:", error);
      setError("Failed to add business. Please try again.");
    }
  }, [getValues, appendBusiness, setError, clearError, clearErrors]);

  const handleRemoveBusiness = useCallback((index: number): void => {
    if (businessFields.length === 1) {
      setError("At least one business entry is required.");
      return;
    }
    removeBusiness(index);
  }, [businessFields.length, removeBusiness, setError]);

  const handleAddSocialMedia = useCallback((businessIndex: number): void => {
    const currentBusinesses = getValues("businesses");
    const currentSocialMedia = currentBusinesses[businessIndex]?.socialMedia || [];
    
    setValue(`businesses.${businessIndex}.socialMedia`, [
      ...currentSocialMedia,
      { platform: "", url: "" }
    ]);
    // Set social media type to has-social-media if not already set
    const currentType = getValues(`businesses.${businessIndex}.socialMediaType`);
    if (!currentType || currentType === "no-social-media") {
      setValue(`businesses.${businessIndex}.socialMediaType`, 'has-social-media');
    }
    // Trigger validation after adding social media
    trigger(`businesses.${businessIndex}.socialMediaType`);
  }, [getValues, setValue, trigger]);

  const handleRemoveSocialMedia = useCallback((businessIndex: number, socialIndex: number): void => {
    const currentBusinesses = getValues("businesses");
    const currentSocialMedia = currentBusinesses[businessIndex]?.socialMedia || [];
    
    const updatedSocialMedia = currentSocialMedia.filter((_: any, index: number) => index !== socialIndex);
    setValue(`businesses.${businessIndex}.socialMedia`, updatedSocialMedia);
  }, [getValues, setValue]);

  
  return (
    <div className="b2b-profile-create-container">
      {showEmailVerifiedBanner && (
        <>
          <div
            className="b2b-email-verified-backdrop"
            onClick={dismissEmailVerifiedBanner}
            aria-hidden="true"
          />
          <div className="b2b-email-verified-popup" role="status" aria-live="polite">
            <div className="b2b-email-verified-popup__icon">
              <CheckCircle size={22} aria-hidden="true" />
            </div>
            <div className="b2b-email-verified-popup__content">
              <div className="b2b-email-verified-popup__title">
                {tAuth("emailVerifiedSuccessfully") || "Email Verified Successfully!"}
              </div>
              <div className="b2b-email-verified-popup__message">
                {emailVerifiedMessage || "Please complete your profile questionnaire below."}
              </div>
            </div>
            <div className="b2b-email-verified-popup__actions">
              <button
                type="button"
                className="b2b-email-verified-popup__ok"
                onClick={dismissEmailVerifiedBanner}
              >
                OK
              </button>
            </div>
          </div>
        </>
      )}
      {/* Header similar to dashboard events */}
      <div className="dashboard-profile-header">
        {/* Only show back button in edit mode - first-time profile creation should not allow dashboard access */}
        {isEditMode && (
          <B2BBackButton
            to="/gcb/dashboard"
            translationKey="dashboard.profile.backToDashboard"
            variant="orange"
            size="medium"
          />
        )}
        <div className="profile-header-content">
          <h1>
            {isEditMode ? tProfile("editTitle") : tProfile("createTitle")}
          </h1>
        </div>
      </div>

      <div className="b2b-profile-create-card">
        <div className="header-content">
          <div className="profile-create-header-row">
            <p className="profile-save-instruction">
              {isEditMode ? tProfile("saveInstruction") : tProfile("createInstruction")}
            </p>
            {/* Auto-save status indicator */}
            {!isEditMode && (
              <div
                className="auto-save-status"
                data-status={saveStatus}
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {saveStatus === 'saving' && (
                  <>
                    <div className="auto-save-spinner" aria-hidden="true" />
                    <span>Saving...</span>
                  </>
                )}
                {saveStatus === 'saved' && lastSavedTime && (
                  <>
                    <CheckCircle size={14} className="auto-save-status__icon auto-save-status__icon--saved" aria-hidden="true" />
                    <span>
                      Saved {lastSavedTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </>
                )}
                {saveStatus === 'error' && (
                  <>
                    <AlertCircle size={14} className="auto-save-status__icon auto-save-status__icon--error" aria-hidden="true" />
                    <span>Save failed</span>
                  </>
                )}
                {saveStatus === 'idle' && lastSavedTime && (
                  <span className="auto-save-status__muted">
                    Last saved: {lastSavedTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="error-message-enhanced" role="alert">
            <div className="error-icon-wrapper">
              <AlertCircle size={20} aria-hidden="true" />
            </div>
            <div className="error-content">
              <h3 className="error-title">Please fix the following issues:</h3>
              <div className="error-text-enhanced">{error}</div>
              <div className="error-help">
                <p>💡 <strong>Tip:</strong> Check the highlighted fields below and complete all required information.</p>
              </div>
            </div>
          </div>
        )}

        {false && (
          <div className="success-message" role="alert">
            <CheckCircle size={16} aria-hidden="true" />
            <span>{t('dashboard.profile.deleteSuccess')}</span>
          </div>
        )}

        <form onSubmit={handleFormSubmitWrapper} className="profile-form" noValidate>
          {/* Member ID Section - Hidden from users, only stored in database and visible to admins */}
          {/* The memberId is still generated and sent to the backend, just not displayed */}
          <input
            {...register("memberId")}
            type="hidden"
            id="memberId"
          />

          {/* Company Information Section */}
          <fieldset className="form-section">
            <legend>{tProfile('yourCompanyInformation')}</legend>
            {businessFields.map((field, index: number) => {
              const getBusinessLabel = (idx: number): string => {
                switch (idx) {
                  case 0:
                    return tProfile('yourFirstCompany');
                  case 1:
                    return tProfile('yourSecondCompany');
                  case 2:
                    return tProfile('yourThirdCompany');
                  case 3:
                    return tProfile('yourFourthCompany');
                  case 4:
                    return tProfile('yourFifthCompany');
                  default:
                    return `${tProfile('yourCompanyInformation')} ${idx + 1}`;
                }
              };

              return (
                <div key={field.id} className="business-block">
                <div className="business-header">
                  <h3>{getBusinessLabel(index)}</h3>
                  {businessFields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveBusiness(index)}
                      className="remove-business"
                      aria-label={`Remove company ${index + 1}`}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </button>
                  )}
                </div>

                <B2BFormField
                  {...getFieldProps(`businesses.${index}.businessName`, 'text', {
                    latinOnly: true,
                    label: tProfile('companyName'),
                    placeholder: tProfile('placeholders.companyName') || 'Enter company name',
                    required: true
                  })}
                />

                {/* Business Category — multi-select checkboxes (up to 3) */}
                <div className="form-group">
                  <input
                    type="hidden"
                    {...register(`businesses.${index}.businessCategory`, {
                      validate: () => {
                        const businessName = getValues(`businesses.${index}.businessName`);
                        if (businessName && businessName.trim()) {
                          const cats: string[] = watch(`businesses.${index}.businessCategory`) || [];
                          if (!Array.isArray(cats) || cats.length === 0) {
                            return 'Please select at least one business category';
                          }
                          if (cats.length > 3) {
                            return 'You can select up to 3 categories per business';
                          }
                        }
                        return true;
                      }
                    })}
                  />
                  <fieldset>
                    <legend className="field-label required">
                      {tProfile('companyCategory')}
                      <span className="required-asterisk" style={{ color: 'red' }}> *</span>
                    </legend>
                    <p className="field-hint" style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 8px 0' }}>
                      {tProfile('companyCategoryHint') || 'You can select up to 3 categories per company'}
                    </p>
                    <div className="chip-selector">
                      {BUSINESS_CATEGORIES.filter(c => c !== 'other').map((category: string) => {
                        const currentCats: string[] = watch(`businesses.${index}.businessCategory`) || [];
                        const isChecked = currentCats.includes(category);
                        const isDisabled = !isChecked && currentCats.length >= 3;

                        return (
                          <div key={category} className="chip-option">
                            <input
                              type="checkbox"
                              value={category}
                              id={`${index}-category-${category}`}
                              checked={isChecked}
                              disabled={isDisabled}
                              onChange={(e) => {
                                const current: string[] = watch(`businesses.${index}.businessCategory`) || [];
                                if (e.target.checked) {
                                  if (current.length < 3) {
                                    setValue(`businesses.${index}.businessCategory`, [...current, category]);
                                  }
                                } else {
                                  setValue(`businesses.${index}.businessCategory`, current.filter(c => c !== category));
                                }
                                trigger(`businesses.${index}.businessCategory`);
                              }}
                            />
                            <label htmlFor={`${index}-category-${category}`} className="chip-label">
                              {tProfile(`businessCategories.${category}`)}
                            </label>
                          </div>
                        );
                      })}
                      {/* "Other" as the last chip option */}
                      {(() => {
                        const currentCats: string[] = watch(`businesses.${index}.businessCategory`) || [];
                        const isOtherChecked = currentCats.includes('other');
                        const isDisabled = !isOtherChecked && currentCats.length >= 3;
                        return (
                          <div className="chip-option">
                            <input
                              type="checkbox"
                              value="other"
                              id={`${index}-category-other`}
                              checked={isOtherChecked}
                              disabled={isDisabled}
                              onChange={(e) => {
                                const current: string[] = watch(`businesses.${index}.businessCategory`) || [];
                                if (e.target.checked) {
                                  if (current.length < 3) {
                                    setValue(`businesses.${index}.businessCategory`, [...current, 'other']);
                                  }
                                } else {
                                  setValue(`businesses.${index}.businessCategory`, current.filter(c => c !== 'other'));
                                  setValue(`businesses.${index}.otherBusinessCategory`, '');
                                }
                                trigger(`businesses.${index}.businessCategory`);
                              }}
                            />
                            <label htmlFor={`${index}-category-other`} className="chip-label">
                              {tProfile('businessCategories.other')}
                            </label>
                          </div>
                        );
                      })()}
                    </div>
                  </fieldset>
                  {errors.businesses?.[index]?.businessCategory && (
                    <div className="error-text" role="alert">
                      {typeof errors.businesses[index]?.businessCategory?.message === 'string'
                        ? errors.businesses[index]?.businessCategory?.message
                        : 'Please select at least one business category'}
                    </div>
                  )}
                </div>

                {/* Other business category input field — shown when "other" is in the selected categories */}
                {(watch(`businesses.${index}.businessCategory`) || []).includes('other') && (
                  <div className="form-group" style={{ marginTop: 'var(--space-4)' }}>
                    <label htmlFor={`${index}-other-business-category`} className="field-label">
                      {tProfile('otherBusinessCategory')}
                    </label>
                    <input
                      {...((): any => {
                        const reg = register(`businesses.${index}.otherBusinessCategory`, {
                          required: (watch(`businesses.${index}.businessCategory`) || []).includes('other') ? 'Please specify your business category' : false
                        });
                        return {
                          ...reg,
                          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                            const stripped = e.target.value.replace(/[\u0400-\u04FF]/g, '');
                            setValue(`businesses.${index}.otherBusinessCategory`, stripped, { shouldValidate: true });
                            reg.onChange({ ...e, target: { ...e.target, value: stripped } });
                          }
                        };
                      })()}
                      id={`${index}-other-business-category`}
                      type="text"
                      className="form-input"
                      placeholder={tProfile('otherBusinessCategoryPlaceholder')}
                    />
                  </div>
                )}

                <div className="form-group">
                  <input
                    type="hidden"
                    {...register(`businesses.${index}.countriesOfOperation`, {
                      validate: (value) => {
                        // Only validate if business name is filled (business is being used)
                        const businessName = getValues(`businesses.${index}.businessName`);
                        if (businessName && businessName.trim()) {
                          // Check if at least one country is selected
                          const countries = value || getValues(`businesses.${index}.countriesOfOperation`) || [];
                          if (!Array.isArray(countries) || countries.length === 0) {
                            return 'At least one country of operation is required';
                          }
                        }
                        return true; // No validation if business name is empty
                      }
                    })}
                  />
                  <fieldset>
                    <legend className="required">
                      {tProfile('countriesOfOperation')}
                      <span className="required-asterisk" style={{ color: 'red' }}> *</span>
                    </legend>
                    <div className="countries-checkbox-container">
                      {COUNTRIES.map((country: string) => {
                        const currentCountries = watch(`businesses.${index}.countriesOfOperation`) || [];
                        const isChecked = currentCountries.includes(country);
                        const isWorldwideActive = currentCountries.includes('worldwide');
                        // Non-worldwide checkboxes are disabled while "worldwide" is selected
                        const isDisabled = country !== 'worldwide' && isWorldwideActive;
                        
                        return (
                          <div key={country} className="country-checkbox-item">
                            <input
                              type="checkbox"
                              value={country}
                              id={`${index}-country-${country}`}
                              className="country-checkbox-input"
                              checked={isChecked}
                              disabled={isDisabled}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                const currentCountries = watch(`businesses.${index}.countriesOfOperation`) || [];
                                let newCountries;
                                if (country === 'worldwide') {
                                  // Worldwide checked → keep only "worldwide"; unchecked → empty list
                                  newCountries = checked ? ['worldwide'] : [];
                                } else {
                                  newCountries = checked
                                    ? [...currentCountries, country]
                                    : currentCountries.filter(c => c !== country);
                                }
                                setValue(`businesses.${index}.countriesOfOperation`, newCountries);
                                trigger(`businesses.${index}.countriesOfOperation`);
                              }}
                            />
                            <label
                              htmlFor={`${index}-country-${country}`}
                              className="country-checkbox-label"
                            >
                              {tProfile(`countries.${country}`)}
                            </label>
                          </div>
                        );
                      })}
                      
                      {/* Other country option */}
                      <div className="country-checkbox-item">
                        <input
                          type="checkbox"
                          value="other"
                          id={`${index}-country-other`}
                          className="country-checkbox-input"
                          checked={watch(`businesses.${index}.countriesOfOperation`)?.includes('other') || false}
                          disabled={watch(`businesses.${index}.countriesOfOperation`)?.includes('worldwide') || false}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const currentCountries = watch(`businesses.${index}.countriesOfOperation`) || [];
                            let newCountries;
                            if (checked) {
                              newCountries = [...currentCountries, 'other'];
                            } else {
                              newCountries = currentCountries.filter(c => c !== 'other');
                              setValue(`businesses.${index}.otherCountry`, '');
                            }
                            setValue(`businesses.${index}.countriesOfOperation`, newCountries);
                            trigger(`businesses.${index}.countriesOfOperation`);
                          }}
                        />
                        <label
                          htmlFor={`${index}-country-other`}
                          className="country-checkbox-label"
                        >
                          {tProfile('countries.other')}
                        </label>
                      </div>
                    </div>
                    
                    {/* Selected countries display */}
                    {watch(`businesses.${index}.countriesOfOperation`)?.length > 0 && (
                      <div className="selected-countries-display" style={{ marginTop: 'var(--space-4)' }}>
                        <label className="field-label">{tProfile('selectedCountries')}</label>
                        <div className="selected-countries-list">
                          {watch(`businesses.${index}.countriesOfOperation`)?.map((country: string) => {
                            // Handle custom country names (not in the predefined list)
                            const predefinedCountries = ['worldwide', 'unitedKingdom', 'unitedStates', 'canada', 'germany', 'france', 'italy', 'spain', 'netherlands', 'switzerland', 'austria', 'belgium', 'denmark', 'sweden', 'norway', 'finland', 'poland', 'czechRepublic', 'hungary', 'portugal', 'greece', 'ireland', 'luxembourg', 'slovakia', 'slovenia', 'croatia', 'bulgaria', 'romania', 'estonia', 'latvia', 'lithuania', 'malta', 'cyprus', 'japan', 'southKorea', 'singapore', 'hongKong', 'australia', 'newZealand', 'brazil', 'mexico', 'argentina', 'chile', 'colombia', 'peru', 'uruguay', 'southAfrica', 'israel', 'turkey', 'russia', 'china', 'india', 'thailand', 'malaysia', 'indonesia', 'philippines', 'vietnam', 'other'];
                            
                            let displayName;
                            if (predefinedCountries.includes(country)) {
                              // If it's a predefined country, try to translate it
                              const translation = tProfile(`countries.${country}`);
                              displayName = translation !== `countries.${country}` ? translation : country;
                            } else {
                              // If it's a custom country name (not in predefined list), use it as is
                              displayName = country;
                            }
                            
                            return (
                              <div key={country} className="selected-country-chip">
                                <span className="country-name">{displayName}</span>
                                <button
                                  type="button"
                                  className="remove-country-btn"
                                  onClick={() => {
                                    const currentCountries = watch(`businesses.${index}.countriesOfOperation`) || [];
                                    const newCountries = currentCountries.filter(c => c !== country);
                                    setValue(`businesses.${index}.countriesOfOperation`, newCountries);
                                  }}
                                  aria-label={`Remove ${displayName}`}
                                >
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                  </svg>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {errors.businesses?.[index]?.countriesOfOperation && (
                      <div className="field-error" role="alert">
                        {errors.businesses[index]?.countriesOfOperation?.message}
                      </div>
                    )}
                  </fieldset>
                </div>

                <div className="form-group">
                  <label htmlFor={`businesses.${index}.businessWebsite`} className="field-label required">
                    {tProfile('companyWebsite')}
                    <span className="required-asterisk" style={{ color: 'red' }}> *</span>
                  </label>
                  <input
                    {...register(`businesses.${index}.businessWebsite`, {
                      validate: (value) => {
                        // Only validate if business name is filled (business is being used)
                        const businessName = getValues(`businesses.${index}.businessName`);
                        if (businessName && businessName.trim()) {
                          // Check if website URL is provided (prioritize the actual value)
                          const hasWebsite = value && value.trim() && 
                                            value.trim() !== "none" && 
                                            value.trim() !== "https://none" && 
                                            value.trim() !== "http://none";
                          
                          // If there's a valid website value, it's always valid
                          if (hasWebsite) {
                            return true;
                          }
                          
                          // If no website value, check if "no-website" is selected
                          // Use getValues to get the current form state
                          const websiteType = getValues(`businesses.${index}.businessWebsiteType`);
                          const isNoWebsite = websiteType === "no-website";
                          
                          // If "no-website" is selected, it's valid
                          if (isNoWebsite) {
                            return true;
                          }
                          
                          // Otherwise, it's required
                          return 'Business website is required. Please enter a website URL or select "No website"';
                        }
                        return true; // No validation if business name is empty
                      }
                    })}
                    id={`businesses.${index}.businessWebsite`}
                    type="url"
                    className="form-input"
                    placeholder={tProfile('placeholders.companyWebsite') || tProfile('placeholders.businessWebsite')}
                    disabled={watch(`businesses.${index}.businessWebsiteType`) === "no-website"}
                    value={(() => {
                      const websiteType = watch(`businesses.${index}.businessWebsiteType`);
                      const websiteValue = watch(`businesses.${index}.businessWebsite`) || "";
                      
                      // If "no-website" is selected, show empty
                      if (websiteType === "no-website") {
                        return "";
                      }
                      
                      // Filter out "none", "https://none", "http://none" values
                      const normalizedValue = websiteValue.trim().toLowerCase();
                      if (normalizedValue === "none" || 
                          normalizedValue === "https://none" || 
                          normalizedValue === "http://none") {
                        return "";
                      }
                      
                      return websiteValue;
                    })()}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Don't allow "none" or "https://none" to be entered
                      if (value && (value.toLowerCase() === "none" || value.toLowerCase() === "https://none" || value.toLowerCase() === "http://none")) {
                        setValue(`businesses.${index}.businessWebsite`, '');
                        setValue(`businesses.${index}.businessWebsiteType`, 'no-website');
                        // Trigger validation after setting the value
                        trigger(`businesses.${index}.businessWebsite`);
                      } else if (value && value.trim()) {
                        // Normalize the URL when user finishes typing (on blur) or when they pause
                        // For now, store the raw value and normalize on blur
                        setValue(`businesses.${index}.businessWebsite`, value);
                        setValue(`businesses.${index}.businessWebsiteType`, 'has-website');
                        // Trigger validation after setting the value to ensure validation runs with updated type
                        trigger(`businesses.${index}.businessWebsite`);
                      } else {
                        // Empty value
                        setValue(`businesses.${index}.businessWebsite`, value);
                        // If value is empty, check current type - if it's not 'no-website', set to 'has-website'
                        // This ensures the type is always set when user is typing
                        const currentType = getValues(`businesses.${index}.businessWebsiteType`);
                        if (currentType !== 'no-website') {
                          setValue(`businesses.${index}.businessWebsiteType`, 'has-website');
                        }
                        // Trigger validation after setting the value to ensure validation runs with updated type
                        trigger(`businesses.${index}.businessWebsite`);
                      }
                    }}
                    onBlur={(e) => {
                      // Normalize URL when user leaves the field
                      const value = e.target.value;
                      if (value && value.trim() && 
                          value.toLowerCase() !== "none" && 
                          value.toLowerCase() !== "https://none" && 
                          value.toLowerCase() !== "http://none") {
                        const normalized = normalizeUrl(value);
                        if (normalized !== value) {
                          setValue(`businesses.${index}.businessWebsite`, normalized);
                          trigger(`businesses.${index}.businessWebsite`);
                        }
                      }
                    }}
                  />
                  {errors.businesses?.[index]?.businessWebsite && (
                    <div className="field-error" role="alert">
                      {errors.businesses[index]?.businessWebsite?.message}
                    </div>
                  )}
                  {/* Hidden input to register businessWebsiteType so getValues can access it */}
                  <input
                    type="hidden"
                    {...register(`businesses.${index}.businessWebsiteType`)}
                  />
                  <div className="checkbox-item">
                    <input
                      type="checkbox"
                      id={`businesses.${index}.no-website`}
                      checked={watch(`businesses.${index}.businessWebsiteType`) === "no-website"}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setValue(`businesses.${index}.businessWebsiteType`, 'no-website');
                          setValue(`businesses.${index}.businessWebsite`, '');
                          // Trigger validation after setting the value
                          trigger(`businesses.${index}.businessWebsite`);
                        } else {
                          setValue(`businesses.${index}.businessWebsiteType`, 'has-website');
                          // Trigger validation after setting the value
                          trigger(`businesses.${index}.businessWebsite`);
                        }
                      }}
                    />
                    <label htmlFor={`businesses.${index}.no-website`}>
                      {tProfile('noWebsite')}
                    </label>
                  </div>
                </div>

                <B2BFormField
                  {...getFieldProps(`businesses.${index}.companyPhoneNumber`, 'phone', {
                    label: tProfile('companyPhoneNumber'),
                    required: false
                  })}
                />

                <B2BFormField
                  {...getFieldProps(`businesses.${index}.businessDescription`, 'textarea', {
                    label: tProfile('companyDescription'),
                    placeholder: tProfile('placeholders.companyDescription') || tProfile('placeholders.businessDescription'),
                    rows: 3,
                    maxLength: 500,
                    latinOnly: true
                  })}
                />
                <div className="character-count">
                  {watch(`businesses.${index}.businessDescription`)?.length || 0}/500 characters
                </div>

                <div className="form-group">
                  <label>{tProfile('companySocialMedia')}</label>
                  <input
                    type="hidden"
                    {...register(`businesses.${index}.socialMediaType`)}
                  />
                  <div className="social-media-container">
                    {watch(`businesses.${index}.socialMediaType`) === "has-social-media" && (
                      <>
                        {(() => {
                          const socialMedia = watch(`businesses.${index}.socialMedia`);
                          const socialMediaArray = Array.isArray(socialMedia) ? socialMedia : [];
                          return socialMediaArray.map((social: any, socialIndex: number) => (
                          <div key={socialIndex} className="social-media-item">
                            <div className="social-media-row">
                              <div className="social-media-platform">
                                <select
                                  {...register(`businesses.${index}.socialMedia.${socialIndex}.platform` as const)}
                                  className="form-select"
                                >
                                  <option value="">Select platform</option>
                                  {SOCIAL_MEDIA_PLATFORMS.map((platform: string) => (
                                    <option key={platform} value={platform}>
                                      {tProfile(`socialMediaPlatforms.${platform}`)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="social-media-url">
                                <input
                                  {...register(`businesses.${index}.socialMedia.${socialIndex}.url` as const)}
                                  type="text"
                                  className="form-input"
                                  placeholder="username or full URL"
                                  onChange={(e) => {
                                    let value = e.target.value;
                                    const platform = watch(`businesses.${index}.socialMedia.${socialIndex}.platform`);
                                    
                                    // If it's already a full URL (starts with http:// or https://), keep it as is
                                    if (value.startsWith('http://') || value.startsWith('https://')) {
                                      setValue(`businesses.${index}.socialMedia.${socialIndex}.url`, value);
                                      return;
                                    }
                                    
                                    // Remove all @ symbols (not just leading ones) to prevent duplicates
                                    value = value.replace(/@/g, '');
                                    
                                    // If value is not empty and doesn't start with http, add @ prefix
                                    if (value.trim() && !value.startsWith('http://') && !value.startsWith('https://')) {
                                      // For platforms that typically use @ handles (Instagram, Twitter/X, TikTok)
                                      const handlePlatforms = ['instagram', 'twitter', 'tiktok'];
                                      if (platform && handlePlatforms.includes(platform.toLowerCase())) {
                                        value = `@${value}`;
                                      }
                                    }
                                    
                                    setValue(`businesses.${index}.socialMedia.${socialIndex}.url`, value);
                                  }}
                                  onBlur={(e) => {
                                    let value = e.target.value;
                                    const platform = watch(`businesses.${index}.socialMedia.${socialIndex}.platform`);
                                    
                                    // If it's a full URL, keep it as is
                                    if (value.startsWith('http://') || value.startsWith('https://')) {
                                      return;
                                    }
                                    
                                    // Remove all @ symbols to prevent duplicates
                                    value = value.replace(/@/g, '').trim();
                                    
                                    // If there's a value, add @ prefix for handle-based platforms
                                    if (value) {
                                      const handlePlatforms = ['instagram', 'twitter', 'tiktok'];
                                      if (platform && handlePlatforms.includes(platform.toLowerCase())) {
                                        setValue(`businesses.${index}.socialMedia.${socialIndex}.url`, `@${value}`);
                                      }
                                    }
                                  }}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveSocialMedia(index, socialIndex)}
                                className="remove-social-media"
                                aria-label="Remove social media"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ));
                        })()}
                        
                        <button
                          type="button"
                          onClick={() => handleAddSocialMedia(index)}
                          className="add-social-media-button"
                          aria-label="Add social media"
                        >
                          <Plus size={16} />
                          {tProfile('addSocialMedia')}
                        </button>
                      </>
                    )}
                    
                    <div className="checkbox-item" style={{ marginTop: '1rem' }}>
                      <input
                        type="checkbox"
                        id={`businesses.${index}.no-social-media`}
                        checked={watch(`businesses.${index}.socialMediaType`) === "no-social-media"}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setValue(`businesses.${index}.socialMediaType`, 'no-social-media');
                            setValue(`businesses.${index}.socialMedia`, []);
                            // Trigger validation after setting the value
                            trigger(`businesses.${index}.socialMediaType`);
                          } else {
                            setValue(`businesses.${index}.socialMediaType`, 'has-social-media');
                            // Trigger validation after setting the value
                            trigger(`businesses.${index}.socialMediaType`);
                          }
                        }}
                      />
                      <label htmlFor={`businesses.${index}.no-social-media`}>
                        None
                      </label>
                    </div>
                  </div>
                  {errors.businesses?.[index]?.socialMediaType && (
                    <div className="field-error" role="alert">
                      {errors.businesses[index]?.socialMediaType?.message}
                    </div>
                  )}
                </div>
              </div>
              );
            })}

            {businessFields.length < 3 && (
              <>
                <p className="add-business-description">
                  {tProfile('addCompanyDescription') || tProfile('addBusinessDescription')}
                </p>
                <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAddBusiness(e);
                }}
                className="add-business-button"
                aria-label="Add another business"
              >
                <Plus size={16} aria-hidden="true" />
                {tProfile('addAnotherCompany')}
              </button>
              </>
            )}
          </fieldset>

          {/* Interests Section */}
          <fieldset className="form-section">
            <legend>{tProfile('interests')}</legend>
            <div className="form-group">
              <input
                type="hidden"
                {...register('interests', {
                  validate: (value) => {
                    // Check if at least one interest is selected
                    const interests = value || watch("interests") || [];
                    if (!Array.isArray(interests) || interests.length === 0) {
                      return 'At least one interest is required';
                    }
                    return true;
                  }
                })}
              />
              <fieldset>
                <legend className="required">
                  {tProfile('interestedInQuestion')}
                  <span className="required-asterisk" style={{ color: 'red' }}> *</span>
                </legend>
                <div className="interests-container">
                  {INTERESTS_OPTIONS.map((interest: string) => {
                    const currentInterests = watch("interests") || [];
                    const isChecked = currentInterests.includes(interest);
                    
                    
                    return (
                      <div key={interest} className="interest-checkbox">
                        <input
                          type="checkbox"
                          value={interest}
                          id={`interest-${interest}`}
                          className="checkbox-input"
                          checked={isChecked}
                          onChange={(e) => {
                            const currentInterests = watch("interests") || [];
                            if (e.target.checked) {
                              const newInterests = [...currentInterests, interest];
                              setValue("interests", newInterests);
                            } else {
                              const newInterests = currentInterests.filter(item => item !== interest);
                              setValue("interests", newInterests);
                            }
                            // Trigger validation after setting the value
                            trigger("interests");
                          }}
                        />
                        <label
                          htmlFor={`interest-${interest}`}
                          className="checkbox-label"
                        >
{tProfile(`interestedInOptions.${interest}`)}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </fieldset>
              {errors.interests && (
                <div className="error-text" role="alert">
                  {errors.interests.message}
                </div>
              )}
            </div>
          </fieldset>

          {/* Personal Information Section */}
          <fieldset className="form-section">
            <legend>{tProfile('personalInformation')}</legend>

            <div className="form-row">
              <B2BFormField
                {...getFieldProps('name', 'text', {
                  label: tAuth('name'),
                  placeholder: tProfile("placeholders.name"),
                  required: true,
                  latinOnly: true
                })}
              />

              <B2BFormField
                {...getFieldProps('surname', 'text', {
                  label: tAuth('surname'),
                  placeholder: tProfile("placeholders.surname"),
                  required: true,
                  latinOnly: true
                })}
              />
            </div>

            <B2BFormField
              {...getFieldProps('phoneNumber', 'phone', {
                label: tAuth('phoneNumber'),
                required: true
                // Phone number is editable - pre-filled from registration but can be changed
              })}
            />

            <B2BFormField
              {...getFieldProps('email', 'email', {
                label: tProfile('emailCannotBeChanged'),
                placeholder: tProfile("placeholders.email"),
                disabled: true,
                required: true
              })}
            />

            {/* Position — multi-select checkboxes (up to 3) */}
            <div className="form-group">
              <input
                type="hidden"
                {...register('position', {
                  validate: () => {
                    const positions = watch("position") || [];
                    if (!Array.isArray(positions) || positions.length === 0) {
                      return 'Please select at least one position';
                    }
                    if (positions.length > 3) {
                      return 'You can select up to 3 positions';
                    }
                    return true;
                  }
                })}
              />
              <fieldset>
                <legend className="field-label required">
                  {tProfile('position')}
                  <span className="required-asterisk" style={{ color: 'red' }}> *</span>
                </legend>
                <p className="field-hint" style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 8px 0' }}>
                  {tProfile('positionHint') || 'You can select up to 3 positions'}
                </p>
                <div className="chip-selector">
                  {POSITION_VALUES.map((pos: string) => {
                    const currentPositions: string[] = watch("position") || [];
                    const isChecked = currentPositions.includes(pos);
                    const isDisabled = !isChecked && currentPositions.length >= 3;

                    return (
                      <div key={pos} className="chip-option">
                        <input
                          type="checkbox"
                          value={pos}
                          id={`position-${pos}`}
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={(e) => {
                            const current: string[] = watch("position") || [];
                            if (e.target.checked) {
                              if (current.length < 3) {
                                setValue("position", [...current, pos]);
                              }
                            } else {
                              setValue("position", current.filter(item => item !== pos));
                            }
                            trigger("position");
                          }}
                        />
                        <label htmlFor={`position-${pos}`} className="chip-label">
                          {tProfile(`positionOptions.${pos}`)}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </fieldset>
              {errors.position && (
                <div className="error-text" role="alert">
                  {typeof errors.position.message === 'string' ? errors.position.message : 'Please select at least one position'}
                </div>
              )}
            </div>


            <div className="form-group">
              <label htmlFor="linkedin" className="field-label">
                {tProfile('linkedin')}
              </label>
              <input
                {...register('linkedin', {
                  validate: (value) => {
                    // Allow "no-linkedin" as a valid value
                    if (value === "no-linkedin") {
                      return true;
                    }
                    // If empty, it's optional - return true
                    if (!value || value.trim() === '') {
                      return true;
                    }
                    // If a value is provided, validate it's a valid URL
                    try {
                      const url = value.startsWith('http://') || value.startsWith('https://') 
                        ? value 
                        : `https://${value}`;
                      new URL(url);
                      return true;
                    } catch {
                      return 'Please enter a valid LinkedIn URL';
                    }
                  }
                })}
                id="linkedin"
                type="url"
                className={`form-input ${errors.linkedin ? 'error' : ''}`}
                placeholder="Enter your LinkedIn URL"
                disabled={watchLinkedin === "no-linkedin"}
                value={(() => {
                  const linkedinValue = watchLinkedin || "";
                  
                  // If "no-linkedin" is selected, show empty
                  if (linkedinValue === "no-linkedin") {
                    return "";
                  }
                  
                  return linkedinValue;
                })()}
                onChange={(e) => {
                  const value = e.target.value;
                  // Don't allow "no-linkedin" to be typed in the input
                  if (value === "no-linkedin") {
                    setValue('linkedin', '', { shouldValidate: true });
                  } else {
                    setValue('linkedin', value, { shouldValidate: true });
                  }
                }}
              />
              {errors.linkedin && (
                <div className="field-error" role="alert">
                  {errors.linkedin.message}
                </div>
              )}
              <div className="checkbox-item">
                <input
                  type="checkbox"
                  id="no-linkedin"
                  checked={watchLinkedin === "no-linkedin"}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setValue('linkedin', 'no-linkedin', { shouldValidate: true });
                    } else {
                      setValue('linkedin', '', { shouldValidate: true });
                    }
                  }}
                />
                <label htmlFor="no-linkedin">
                  {tProfile('noLinkedIn')}
                </label>
              </div>
            </div>

          </fieldset>


          {/* Submit Button */}
          <div className="form-actions">
            <B2BSaveProfileButton
              disabled={isSubmitting || isLoading}
              isLoading={isLoading}
              size="large"
              variant="primary"
              showIcon={true}
            >
              {isEditMode ? tProfile('saveProfile') : tProfile('createProfile')}
            </B2BSaveProfileButton>
            
            {isEditMode && (
              <B2BDeleteProfileButton
                onSuccess={() => {
                  // Handle success - user will be logged out automatically
                }}
                onError={(error) => {
                  setError(error);
                }}
                size="medium"
                variant="primary"
                showIcon={true}
                disabled={isLoading}
              />
            )}
          </div>
        </form>
      </div>

      {/* Success Modal - Only shown for approved users editing their profile */}
      {showSuccessModal && isEditMode && (user?.isApproved || user?.isAdmin) && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <div className="success-modal-header">
              <div className="success-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
              </div>
              <h3>
                {isEditMode ? tProfile('editSuccessTitle') : tProfile('createSuccessTitle')}
              </h3>
            </div>
            <div className="success-modal-content">
              <p>
                {isEditMode ? tProfile('editSuccessMessage') : tProfile('createSuccessMessage')}
              </p>
              <div className="success-modal-actions">
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="success-modal-button success-modal-button--secondary"
                >
                  {tProfile('stayOnPage')}
                </button>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    // Modal only shows for approved users editing, so always go to dashboard
                    navigate("/gcb/dashboard", { replace: true });
                  }}
                  className="success-modal-button success-modal-button--primary"
                >
                  {tProfile('goToDashboard')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default B2BProfileCreate;
